import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import {
  accountsContractSplitStatus,
  accountsPartyWeightStatus,
  accountsPraveshParchiStatus,
  accountsVoucherPraveshParchiStatus,
  buildContractTermOverridesPayload,
  buildSettlementReviewRows,
  buildTruckReviewRows,
  formatInr,
  labelFromOptions,
  pickContractTermsFields,
  pruneFormForPurchase,
  purchaseAllocKey,
  purchaseNeedsFreightVoucher,
  purchaseRequiresFreightSettlement,
  defaultFreightRateFromPurchase,
  extractFrozenPartyExpenseFromDeductions,
  groupVouchersByPurchase,
  mergeMaterialVouchers,
  voucherGroupMatchesVoucherId,
  purchaseRegisterRelatedParts,
  ratesFromPurchaseAllocations,
  buildPurchaseVoucherAmountSummary,
  partyFreightDisplayLabel,
} from "../lib/accountsSettlement";
import {
  buildPurchaseFreightFinalizePayload,
  buildPurchaseFreightPreviewParams,
  FREIGHT_PAYEE_OPTIONS,
  resolveFreightTdsPan,
} from "../lib/freightSettlement";
import { formatIndiaDateTime } from "../lib/indiaTime";
import { cn } from "../lib/utils";
import { PageHeader, SectionCard, Pill, StatePill, ModuleTabs, EmptyState } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SettlementReviewTable from "../components/SettlementReviewTable";
import SettlementStepper from "../components/SettlementStepper";
import FreightTdsFields from "../components/FreightTdsFields";
import { toast } from "sonner";
import {
  FileCheck2,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader2,
  LayoutGrid,
  ArrowDownToLine,
  ArrowUpFromLine,
  BarChart3,
  AlertTriangle,
  Scale,
  Link2,
  Wallet,
  Receipt,
  Truck,
  Users,
  RefreshCw,
  Lock,
  ShieldCheck,
  Landmark,
  BookOpen,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, hasPermission, canEditVouchers } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import AccountsPartyWeightEditor from "../components/AccountsPartyWeightEditor";
import AccountsLinkedContractsEditor from "../components/AccountsLinkedContractsEditor";
import AccountsEmandiSection, { buildEmandiFinalizePayload } from "../components/AccountsEmandiSection";
import PurchaseTraceLine from "../components/PurchaseTraceLine";
import CompactTableFilters from "../components/CompactTableFilters";
import DataTablePagination from "../components/DataTablePagination";
import VoucherDocumentActions from "../components/VoucherDocumentActions";
import PurchaseVoucherPrintBar from "../components/PurchaseVoucherPrintBar";
import AdminEntityActions from "../components/AdminEntityActions";
import VoucherExportDialog from "../components/VoucherExportDialog";
import TallySyncBadge from "../components/TallySyncBadge";
import TallySyncMappingDialog from "../components/TallySyncMappingDialog";
import { tallyDirectSyncBlockedReason } from "../lib/tallyApproval";
import { useAccountsBootstrap } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { useWSEvent } from "../context/WebSocketContext";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { PENDING_PURCHASE_COLUMNS, VOUCHER_COLUMNS } from "./accountsColumns";
import AccountsTabStub from "./accounts/AccountsTabStub";
import AccountsCashLedgerPanel from "./accounts/AccountsCashLedgerPanel";
import AccountsLabourSettlementPanel from "./accounts/AccountsLabourSettlementPanel";
import AccountsSalesSettlementPanel from "./accounts/AccountsSalesSettlementPanel";
import PartyExpenseEditor from "../components/PartyExpenseEditor";
import SupplierLandedCostCard from "../components/SupplierLandedCostCard";
import AssignSupplierDialog from "../components/AssignSupplierDialog";
import PendingFreightSettlementEditor from "../components/PendingFreightSettlementEditor";

const defaultForm = (p) => {
  const allocs = p.contract_allocations || [];
  const multi = allocs.length > 1;
  const defaultFreightRate = defaultFreightRateFromPurchase(p);
  const { allocation_rates, revised_rate } = ratesFromPurchaseAllocations(p);
  return {
    revised_rate: multi ? "" : revised_rate,
    allocation_rates,
    contract_terms: {},
    contract_terms_baseline: {},
    _terms_seeded: false,
    weight_cut_pct: p.weight_cut_pct ?? 0,
    price_cut_pct: p.price_cut_pct ?? 0,
    deduction_scope: "COMPLETE",
    freight_rate_per_qtl: defaultFreightRate !== "" ? String(defaultFreightRate) : "",
    freight_amount: "",
    freight_calc_mode: "PER_QTL",
    party_net_weight_qtl: p.party_net_weight_qtl != null ? String(p.party_net_weight_qtl) : "",
    freight_allowance_code: p.freight_allowance_code || "",
    freight_billable_basis: p.freight_billable_basis || "UNLOAD",
    shortage_allowance_mode: p.shortage_allowance_mode || "",
    shortage_allowance_value:
      p.shortage_allowance_value != null ? String(p.shortage_allowance_value) : "",
    party_kind: p.party_kind || "",
    party_name: p.party_name || "",
    farmer_bank_name: "",
    farmer_bank_account_no: "",
    farmer_bank_ifsc: "",
    party_id: p.party_id || null,
    form_6r_no: "",
    invoice_no: "",
    invoice_date: "",
    party_bank_name: "",
    party_bank_account_no: "",
    party_bank_ifsc: "",
    form_9r_no: "",
    mandi_gate_pass_no: "",
    pravesh_parchi_no: "",
    erp_gate_pass_code: p.gate_pass_code || "",
    freight_slip: {},
    _freight_slip_seeded: false,
    freight_finalize: true,
    freight_waived: false,
    tds_review_lines: [],
  };
};

/** Merge purchase API vouchers with paginated bootstrap rows (enriched top-level freight fields). */
function vouchersForEditSeed(purchaseVouchers = [], bootstrapVouchers = [], focusVoucher = null) {
  const byId = new Map();
  for (const row of purchaseVouchers) {
    if (row?.id != null) byId.set(row.id, { ...row });
  }
  for (const row of bootstrapVouchers) {
    if (row?.id == null) continue;
    const prev = byId.get(row.id);
    byId.set(row.id, prev ? { ...prev, ...row } : { ...row });
  }
  if (focusVoucher?.id != null) {
    const prev = byId.get(focusVoucher.id);
    byId.set(focusVoucher.id, prev ? { ...prev, ...focusVoucher } : { ...focusVoucher });
  }
  if (byId.size) return [...byId.values()];
  if (focusVoucher?.id != null) return [focusVoucher];
  return [...bootstrapVouchers];
}

function seedFormFromPurchaseForEdit(p, vouchers = [], focusVoucher = null) {
  const base = defaultForm(p);
  const merged = vouchersForEditSeed(vouchers, [], focusVoucher);
  const material = mergeMaterialVouchers(merged.filter((row) => row.voucher_type === "MATERIAL"))
    || (focusVoucher?.voucher_type === "MATERIAL" ? focusVoucher : null);
  const freight = merged.find((row) => row.voucher_type === "FREIGHT")
    || (focusVoucher?.voucher_type === "FREIGHT" ? focusVoucher : null);

  if (material) {
    base.weight_cut_pct = material.weight_cut_pct ?? base.weight_cut_pct;
    base.price_cut_pct = material.price_cut_pct ?? base.price_cut_pct;
    if ((p.contract_allocations?.length ?? 0) <= 1 && material.rate_per_qtl != null) {
      base.revised_rate = String(material.rate_per_qtl);
    }
    const ded = material.deductions || {};
    if (ded.scope) base.deduction_scope = ded.scope;
    if (ded.freight_rate_override != null) base.freight_rate_per_qtl = String(ded.freight_rate_override);
    if (ded.freight_amount_override != null) {
      base.freight_amount = String(ded.freight_amount_override);
      base.freight_calc_mode = "FIXED";
    }
    const snapParty = ded.settlement_snapshot?.party_net_weight_qtl;
    if (snapParty != null) {
      base.party_net_weight_qtl = String(snapParty);
    }
    base._settlement_frozen = Boolean(ded.settlement_frozen);
    for (const line of ded.allocation_lines || []) {
      const cid = String(line.purchase_contract_id);
      if (line.rate_per_qtl != null) {
        base.allocation_rates[cid] = String(line.rate_per_qtl);
      }
    }
    const termsById = ded.accounts_contract_terms_by_id || {};
    if (Object.keys(termsById).length) {
      base.contract_terms = {};
      base.contract_terms_baseline = {};
      for (const [cid, terms] of Object.entries(termsById)) {
        const picked = pickContractTermsFields(terms);
        base.contract_terms[cid] = { ...picked };
        base.contract_terms_baseline[cid] = { ...picked };
      }
      base._terms_seeded = true;
    }
    const emandi = ded.emandi_linkage || {};
    const block = emandi.farmer || emandi.party || {};
    base.form_6r_no = block.form_6r_no || material.form_6r_no || "";
    base.invoice_no = block.invoice_no || material.invoice_no || "";
    base.invoice_date = (block.invoice_date || material.invoice_date || "").slice(0, 10);
    base.form_9r_no = block.form_9r_no || material.form_9r_no || "";
    base.mandi_gate_pass_no = block.mandi_gate_pass_no || material.mandi_gate_pass_no || "";
    base.pravesh_parchi_no = block.pravesh_parchi_no || material.pravesh_parchi_no || "";
    if (block.bank_name || material.bank_name) base.party_bank_name = block.bank_name || material.bank_name || "";
    if (block.bank_account_no || material.bank_account_no) {
      base.party_bank_account_no = block.bank_account_no || material.bank_account_no || "";
    }
    if (block.bank_ifsc || material.bank_ifsc) base.party_bank_ifsc = block.bank_ifsc || material.bank_ifsc || "";
    const frozenExpenses = extractFrozenPartyExpenseFromDeductions(ded);
    if (frozenExpenses?.lines?.length) {
      base.party_expense_manual = true;
      base._party_expense_frozen = Boolean(frozenExpenses.frozen);
      base.party_expense_lines = frozenExpenses.lines.map((ln) => ({
        code: ln.code || "",
        label: ln.label || "",
        amount: ln.amount != null ? String(ln.amount) : "",
        effect: ln.effect || "subtract",
      }));
    }
  }

  if (freight) {
    const ded = freight.deductions || {};
    const slip = ded.freight_slip || freight.freight_slip || {};
    if (Object.keys(slip).length) {
      base.freight_slip = { ...slip };
      if (!base.freight_slip.accounts_note && ded.accounts_note) {
        base.freight_slip.accounts_note = ded.accounts_note;
      }
      base._freight_slip_seeded = true;
    }
    const slipRate = slip.freight_rate_per_qtl ?? freight.rate_per_qtl;
    if (slipRate != null && slipRate !== "" && !base.freight_rate_per_qtl) {
      base.freight_rate_per_qtl = String(slipRate);
    }
    // A filled freight slip is finalized on save. A current DRAFT status must not
    // keep the next update as a draft.
    base.freight_finalize = true;
  }

  if (material?.freight_settlement_waived) {
    base.freight_waived = true;
    base.freight_finalize = false;
    base.freight_rate_per_qtl = "";
    base.freight_amount = "";
  }

  return base;
}

function formSeedForPurchase(p) {
  const vouchers = p?.vouchers || [];
  if (vouchers.length) {
    return seedFormFromPurchaseForEdit(p, vouchers);
  }
  return defaultForm(p);
}

function buildSettlementPreviewParams(p, form) {
  const f = pruneFormForPurchase(p, form);
  const multi = (p.contract_allocations?.length ?? 0) > 1;
  const freightWaived = Boolean(f.freight_waived);
  const params = {
    weight_cut_pct: Number(f.weight_cut_pct) || 0,
    price_cut_pct: Number(f.price_cut_pct) || 0,
    deduction_scope: f.deduction_scope || "COMPLETE",
    freight_rate_per_qtl:
      !freightWaived && f.freight_rate_per_qtl !== "" ? Number(f.freight_rate_per_qtl) : undefined,
    freight_amount:
      !freightWaived && f.freight_amount !== "" ? Number(f.freight_amount) : undefined,
    party_net_weight_qtl: f.party_net_weight_qtl !== "" ? Number(f.party_net_weight_qtl) : undefined,
    freight_allowance_code: !freightWaived ? (f.freight_allowance_code || undefined) : undefined,
    freight_billable_basis: !freightWaived ? (f.freight_billable_basis || undefined) : undefined,
    shortage_allowance_mode: !freightWaived ? (f.shortage_allowance_mode || undefined) : undefined,
    shortage_allowance_value:
      !freightWaived
      && f.shortage_allowance_value !== ""
      && f.shortage_allowance_value != null
        ? Number(f.shortage_allowance_value)
        : undefined,
    freight_waived: freightWaived ? true : undefined,
  };
  if (!freightWaived) {
    Object.assign(params, buildPurchaseFreightPreviewParams(f, null));
  }
  if (multi) {
    const rates = Object.entries(f.allocation_rates || {})
      .filter(([, v]) => v !== "" && v != null)
      .map(([id, v]) => ({ purchase_contract_id: Number(id), revised_rate: Number(v) }));
    if (rates.length) params.allocation_rates = JSON.stringify(rates);
  } else if (f.revised_rate !== "" && f.revised_rate != null) {
    params.revised_rate = Number(f.revised_rate);
  }
  if (f._terms_seeded) {
    const overrides = buildContractTermOverridesPayload(f, p);
    if (overrides.length > 0) {
      params.contract_term_overrides = JSON.stringify(overrides);
    }
  }
  if ((f.party_expense_manual || f._party_expense_frozen) && f.party_expense_lines?.length) {
    const expenseLines = f.party_expense_lines
      .filter((ln) => {
        const amt = Number(ln.amount);
        const rate = Number(ln.rate);
        return (Number.isFinite(amt) && amt > 0) || (Number.isFinite(rate) && rate > 0 && ln.basis);
      })
      .map((ln) => ({
        code: ln.code,
        label: ln.label,
        amount: Number(ln.amount) || 0,
        effect: ln.effect || "add",
        basis: ln.basis || undefined,
        rate: ln.rate != null && ln.rate !== "" ? Number(ln.rate) : undefined,
      }));
    if (expenseLines.length) {
      params.party_expense_overrides = JSON.stringify(expenseLines);
    }
  }
  return params;
}

function buildFinalizePayload(p, form) {
  const f = pruneFormForPurchase(p, form);
  const multi = (p.contract_allocations?.length ?? 0) > 1;
  const freightWaived = Boolean(f.freight_waived);
  const payload = {
    weight_cut_pct: Number(f.weight_cut_pct) || 0,
    price_cut_pct: Number(f.price_cut_pct) || 0,
    deduction_scope: f.deduction_scope || "COMPLETE",
    freight_rate_per_qtl:
      !freightWaived && f.freight_rate_per_qtl !== "" ? Number(f.freight_rate_per_qtl) : undefined,
    freight_amount:
      !freightWaived && f.freight_amount !== "" ? Number(f.freight_amount) : undefined,
    party_net_weight_qtl: f.party_net_weight_qtl !== "" ? Number(f.party_net_weight_qtl) : undefined,
    freight_allowance_code: !freightWaived ? (f.freight_allowance_code || undefined) : undefined,
    freight_billable_basis: !freightWaived ? (f.freight_billable_basis || undefined) : undefined,
    shortage_allowance_mode: !freightWaived ? (f.shortage_allowance_mode || undefined) : undefined,
    shortage_allowance_value:
      !freightWaived
      && f.shortage_allowance_value !== ""
      && f.shortage_allowance_value != null
        ? Number(f.shortage_allowance_value)
        : undefined,
    freight_waived: freightWaived,
  };
  if (multi) {
    payload.allocation_rates = Object.entries(f.allocation_rates || {})
      .filter(([, v]) => v !== "" && v != null)
      .map(([id, v]) => ({ purchase_contract_id: Number(id), revised_rate: Number(v) }));
  } else if (f.revised_rate !== "" && f.revised_rate != null) {
    payload.revised_rate = Number(f.revised_rate);
  }
  if (f._terms_seeded) {
    const overrides = buildContractTermOverridesPayload(f, p);
    if (overrides.length > 0) {
      payload.contract_term_overrides = overrides;
    }
  }
  const emandi = buildEmandiFinalizePayload(f);
  if (emandi) payload.emandi = emandi;
  if ((f.party_expense_manual || f._party_expense_frozen) && f.party_expense_lines?.length) {
    payload.party_expense_overrides = f.party_expense_lines
      .filter((ln) => {
        const amt = Number(ln.amount);
        const rate = Number(ln.rate);
        return (Number.isFinite(amt) && amt > 0) || (Number.isFinite(rate) && rate > 0 && ln.basis);
      })
      .map((ln) => ({
        code: ln.code,
        label: ln.label,
        amount: Number(ln.amount) || 0,
        effect: ln.effect || "subtract",
        basis: ln.basis || undefined,
        rate: ln.rate != null && ln.rate !== "" ? Number(ln.rate) : undefined,
      }));
  }
  if (!freightWaived && (purchaseNeedsFreightVoucher(p) || f.freight_rate_per_qtl || f.freight_amount)) {
    const freightPayload = buildPurchaseFreightFinalizePayload(f);
    if (Object.keys(freightPayload).length) {
      payload.freight_slip = freightPayload;
      payload.freight_finalize = true;
    }
  }
  if (Array.isArray(f.tds_review_lines) && f.tds_review_lines.length) {
    payload.tds_review_lines = f.tds_review_lines
      .filter((ln) => !(freightWaived && String(ln.scope || "").toUpperCase() === "FREIGHT"))
      .map((ln) => ({
      line_key: ln.line_key,
      scope: ln.scope || "MATERIAL",
      line_kind: ln.line_kind || undefined,
      nature_code: ln.nature_code || "NONE",
      payment_code: ln.payment_code || undefined,
      label: ln.label || undefined,
      payable_code: ln.payable_code || undefined,
      gross_amount: Number(ln.gross_amount) || 0,
      taxable_amount: Number(ln.taxable_amount) || 0,
      tds_pct: Number(ln.tds_pct) || 0,
      tds_amount: Number(ln.tds_amount) || 0,
      tds_applicable: Boolean(ln.tds_applicable) || Number(ln.tds_amount) > 0,
      reason: ln.reason || undefined,
      manual: Boolean(ln.manual),
    }));
  }
  return payload;
}

export default function AccountsPage({ defaultTab = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { optionsFor } = useLookups();
  const [activeTab, setActiveTab] = useState(defaultTab || "pending");
  const [finalizeForms, setFinalizeForms] = useState({});
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedPurchaseLoading, setSelectedPurchaseLoading] = useState(false);
  const [pendingSearch, setPendingSearch] = useState("");
  const [parchiPendingOnly, setParchiPendingOnly] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [voucherSearch, setVoucherSearch] = useState("");
  const [focusVoucherPurchaseCode, setFocusVoucherPurchaseCode] = useState(null);
  const [moduleTab, setModuleTab] = useState("settlement");
  const { pending: pendingList, vouchers: voucherList, reload: reloadAccounts, aggregates: accountsAggregates } = useAccountsBootstrap({
    pendingColumns: PENDING_PURCHASE_COLUMNS,
    voucherColumns: VOUCHER_COLUMNS,
    pendingSearch,
    voucherSearch,
    enabled: moduleTab === "settlement",
    include: "both",
  });

  useBootstrapWS(["purchase", "voucher", "tally"], moduleTab === "settlement" ? reloadAccounts : () => {});

  useWSEvent("tally", (evt) => {
    if (evt?.action !== "sync_result") return;
    const code = evt.entity_code || (evt.entity_id != null ? `#${evt.entity_id}` : "Voucher");
    if (evt.success) {
      toast.success(`${code} synced to Tally`);
    } else {
      const detail = (evt.tally_error_message || evt.message || "Sync failed").split("\n")[0];
      toast.error(`${code}: ${detail}`);
    }
  });

  const pendingRows = pendingList.table.rows;
  const filteredPendingRows = useMemo(() => {
    let rows = pendingRows;
    if (parchiPendingOnly) {
      rows = rows.filter((p) => accountsPraveshParchiStatus(p).pending);
    }
    return rows;
  }, [pendingRows, parchiPendingOnly]);

  const voucherRows = voucherList.table.rows;
  const groupedVoucherRows = useMemo(() => groupVouchersByPurchase(voucherRows), [voucherRows]);

  const selectedVoucherGroup = useMemo(
    () => groupedVoucherRows.find((group) => voucherGroupMatchesVoucherId(group, selectedVoucherId)) || null,
    [groupedVoucherRows, selectedVoucherId],
  );

  useEffect(() => {
    if (!pendingRows.length) {
      // Keep current selection while bootstrap is refreshing — clearing here
      // remounted the review panel mid-save and forced a second Next click.
      if (pendingList.loading) return;
      setSelectedPurchaseId(null);
      setSelectedPurchase(null);
      return;
    }
    const stillThere = pendingRows.some((p) => p.id === selectedPurchaseId);
    if (!stillThere) setSelectedPurchaseId(pendingRows[0].id);
  }, [pendingRows, selectedPurchaseId, pendingList.loading]);

  useEffect(() => {
    if (!groupedVoucherRows.length) {
      setSelectedVoucherId(null);
      return;
    }
    if (focusVoucherPurchaseCode) {
      const match = groupedVoucherRows.find((g) => g.purchase_code === focusVoucherPurchaseCode);
      if (match?.primaryId) {
        setSelectedVoucherId(match.primaryId);
        setFocusVoucherPurchaseCode(null);
        return;
      }
    }
    const stillThere = groupedVoucherRows.some((g) => voucherGroupMatchesVoucherId(g, selectedVoucherId));
    if (!stillThere && groupedVoucherRows[0]?.primaryId) {
      setSelectedVoucherId(groupedVoucherRows[0].primaryId);
    }
  }, [groupedVoucherRows, selectedVoucherId, focusVoucherPurchaseCode]);

  const loadSelectedPurchase = useCallback(async (pid, { preserveForm = false } = {}) => {
    if (!pid) return null;
    setSelectedPurchaseLoading(true);
    try {
      const { data } = await api.get(`/purchases/${pid}`);
      setSelectedPurchase(data);
      setFinalizeForms((prev) => ({
        ...prev,
        [pid]: preserveForm && prev[pid] ? prev[pid] : formSeedForPurchase(data),
      }));
      return data;
    } catch (err) {
      toast.error(extractError(err));
      return null;
    } finally {
      setSelectedPurchaseLoading(false);
    }
  }, []);

  const clearVoucherDeepLink = useCallback(() => {
    if (!searchParams.get("voucher")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("voucher");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab === "pending" && selectedPurchaseId) {
      clearVoucherDeepLink();
    }
  }, [activeTab, selectedPurchaseId, clearVoucherDeepLink]);

  useEffect(() => {
    if (!selectedPurchaseId) return undefined;
    let cancelled = false;
    setSelectedPurchaseLoading(true);
    api
      .get(`/purchases/${selectedPurchaseId}`)
      .then((r) => {
        if (cancelled) return;
        setSelectedPurchase(r.data);
        setFinalizeForms((prev) => ({
          ...prev,
          [selectedPurchaseId]: prev[selectedPurchaseId] || formSeedForPurchase(r.data),
        }));
      })
      .catch((err) => {
        if (!cancelled) toast.error(extractError(err));
      })
      .finally(() => {
        if (!cancelled) setSelectedPurchaseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPurchaseId]);

  const refreshSelectedPurchase = useCallback(async () => {
    if (!selectedPurchaseId) return;
    await loadSelectedPurchase(selectedPurchaseId, { preserveForm: true });
    reloadAccounts();
  }, [selectedPurchaseId, loadSelectedPurchase, reloadAccounts]);

  const selectPendingPurchase = useCallback((purchaseId) => {
    setSelectedPurchaseId(purchaseId);
    clearVoucherDeepLink();
    setActiveTab("pending");
  }, [clearVoucherDeepLink]);

  const finalize = async (pid) => {
    const f = finalizeForms[pid];
    if (!f) return;
    const purchase = selectedPurchase?.id === pid ? selectedPurchase : pendingRows.find((row) => row.id === pid);
    try {
      const { data } = await api.post(
        `/purchases/${pid}/accounts-finalize`,
        buildFinalizePayload(purchase || { contract_allocations: [] }, f),
      );
      const vouchers = data?.vouchers || [];
      const freight = vouchers.find((v) => v.voucher_type === "FREIGHT");
      const freightFinalized = freight?.freight_status === "FINALIZED" || freight?.deductions?.freight_status === "FINALIZED";
      if (f.freight_waived) {
        toast.success("Voucher saved — freight waived (no freight voucher)");
      } else if (freight && freightFinalized) {
          toast.success(`Voucher saved — material + freight ${freight.code}`);
        } else if (freight) {
          toast.success(`Voucher saved — freight ${freight.code} is still a draft`);
        } else if (purchaseNeedsFreightVoucher(purchase)) {
        toast.warning("Material voucher saved — enter freight in step 5 to create freight voucher");
      } else {
        toast.success("Voucher saved");
      }
      reloadAccounts();
      if (purchase?.code) {
        setFocusVoucherPurchaseCode(purchase.code);
      }
      const material = vouchers.find((v) => v.voucher_type === "MATERIAL");
      const openId = material?.id || freight?.id;
      if (openId) {
        setSelectedVoucherId(openId);
        setSearchParams({ voucher: String(openId) }, { replace: true });
      }
      setActiveTab("vouchers");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const updateForm = (pid, field, value) => {
    setFinalizeForms((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], [field]: value },
    }));
  };

  const patchForm = (pid, patch) => {
    setFinalizeForms((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], ...patch },
    }));
  };

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "vouchers" || tab === "pending") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const vid = Number(searchParams.get("voucher"));
    if (!vid || Number.isNaN(vid)) return;
    setActiveTab("vouchers");
    setSelectedVoucherId(vid);
  }, [searchParams]);

  useEffect(() => {
    const vid = Number(searchParams.get("voucher"));
    if (!vid || Number.isNaN(vid) || activeTab !== "vouchers") return;
    const match = voucherRows.find((v) => v.id === vid);
    if (match) {
      const group = groupedVoucherRows.find((g) => voucherGroupMatchesVoucherId(g, vid));
      setSelectedVoucherId(group?.primaryId || vid);
    }
  }, [searchParams, voucherRows, groupedVoucherRows, activeTab]);

  const openVoucher = useCallback((voucherId) => {
    setSelectedVoucherId(voucherId);
    setActiveTab("vouchers");
    setSearchParams({ voucher: String(voucherId) }, { replace: true });
  }, [setSearchParams]);

  const canFinalize = hasPermission(user, "purchases.accounts_finalize");
  const canEditVoucher = canEditVouchers(user);
  const canTallySync = hasPermission(user, "tally.sync") || canEditVoucher;
  const [bulkTallySyncTargets, setBulkTallySyncTargets] = useState(null);

  const syncSelectedVouchersToTally = () => {
    const targets = [];
    if (selectedVoucherGroup?.material?.id) {
      targets.push({ id: selectedVoucherGroup.material.id, code: selectedVoucherGroup.material.code });
    }
    if (selectedVoucherGroup?.freight?.id) {
      targets.push({ id: selectedVoucherGroup.freight.id, code: selectedVoucherGroup.freight.code });
    }
    for (const extra of selectedVoucherGroup?.extras || []) {
      if (extra?.id) targets.push({ id: extra.id, code: extra.code || `#${extra.id}` });
    }
    for (const payment of selectedVoucherGroup?.payments || []) {
      if (payment?.id) targets.push({ id: payment.id, code: payment.code || `#${payment.id}` });
    }
    if (!targets.length && selectedVoucherId) {
      const row = voucherRows.find((v) => v.id === selectedVoucherId);
      targets.push({ id: selectedVoucherId, code: row?.code || `#${selectedVoucherId}` });
    }
    if (!targets.length) {
      toast.error("Select vouchers to sync");
      return;
    }
    const blockedRow = [selectedVoucherGroup?.material, selectedVoucherGroup?.freight, selectedVoucher, ...(selectedVoucherGroup?.extras || []), ...(selectedVoucherGroup?.payments || [])]
      .filter(Boolean)
      .find((row) => tallyDirectSyncBlockedReason(row));
    if (blockedRow) {
      toast.error(tallyDirectSyncBlockedReason(blockedRow));
      return;
    }
    setBulkTallySyncTargets(targets);
  };
  const selectedVoucher = selectedVoucherGroup?.material
    || selectedVoucherGroup?.freight
    || voucherRows.find((v) => v.id === selectedVoucherId)
    || null;
  const selectedForm = selectedPurchaseId ? finalizeForms[selectedPurchaseId] : null;
  const registerPayableRows = useMemo(
    () => groupedVoucherRows.flatMap((group) => {
      const rows = [];
      if (group.material) rows.push(group.material);
      if (group.freight) rows.push(group.freight);
      for (const extra of group.extras || []) rows.push(extra);
      for (const payment of group.payments || []) rows.push(payment);
      return rows;
    }),
    [groupedVoucherRows],
  );
  const totalPayable = registerPayableRows
    .filter((v) => v.voucher_type === "MATERIAL" || v.voucher_type === "FREIGHT")
    .reduce((a, v) => a + (v.net_payable || 0), 0);
  const outstanding = registerPayableRows
    .filter((v) => v.voucher_type === "MATERIAL")
    .reduce((a, v) => a + (v.net_payable || 0), 0);
  const totalReceivable = Number(accountsAggregates?.total_receivable || 0);

  const moduleTabs = [
    {
      value: "settlement",
      label: "Purchase settlement",
      icon: LayoutGrid,
      hint: "Truck finalize, voucher register",
      badge: pendingList.meta.count || undefined,
    },
    {
      value: "sales",
      label: "Sales settlement",
      icon: Truck,
      hint: "Pending review → voucher register",
      badge: undefined,
    },
    {
      value: "labour",
      label: "Labour settlement",
      icon: Users,
      hint: "Daily batches — amounts sync to Tally",
    },
    {
      value: "payments",
      label: "Cash ledger",
      icon: ArrowUpFromLine,
      hint: "Cash in / cash out",
      badge: accountsAggregates.cash_count || undefined,
    },
    {
      value: "receipts",
      label: "Bank",
      icon: ArrowDownToLine,
      hint: "Bank payments & receipts (BP/BR)",
      badge: accountsAggregates.receipt_count || undefined,
    },
    {
      value: "reports",
      label: "Reports",
      icon: BarChart3,
      hint: "Operational reports (books in Tally)",
      preview: true,
    },
    {
      value: "tds",
      label: "TDS register",
      icon: FileCheck2,
      hint: "Codes 1023–1031 · Form 140 — nothing missed",
      href: "/accounts/tds",
    },
    {
      value: "bank",
      label: "Bank reconciliation",
      icon: Landmark,
      hint: "HDFC upload · bill-by-bill allocation",
      href: "/accounts/bank-reconciliation",
    },
    {
      value: "ledger",
      label: "General ledger",
      icon: BookOpen,
      hint: "Any CoA ledger · parties · TDS · bank/cash · Excel / PDF",
      href: "/accounts/ledger",
    },
  ];

  const renderModuleStub = () => {
    if (moduleTab === "payments" || moduleTab === "receipts") {
      return null;
    }
    if (moduleTab === "reports") {
      return (
        <AccountsTabStub
          testId="accounts-reports"
          title="Operational reports"
          description="ERP operational snapshots. Full books, day book, and ledgers are maintained in Tally Prime."
          icon={BarChart3}
          features={[
            { title: "Party ageing (ops view)", hint: "From open vouchers in ERP." },
            { title: "Export to Excel", hint: "Reusable saved views." },
            { title: "Tally is source of truth", hint: "Sync vouchers — do not double-post in ERP GL." },
          ]}
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 sm:space-y-8 min-w-0 max-w-full overflow-x-hidden" data-testid="accounts-page">
      <AccountsModuleHeader
        pendingCount={pendingList.meta.count}
        voucherCount={voucherList.meta.count}
        totalPayable={totalPayable}
        outstanding={outstanding}
        totalReceivable={totalReceivable}
        praveshParchiPendingCount={accountsAggregates?.pravesh_parchi_pending_count}
      />

      {/* Same horizontal head tabs for every Accounts module — no left side rail */}
      <div className="space-y-4 min-w-0">
        <ModuleTabs
          layout="bar"
          items={moduleTabs}
          value={moduleTab}
          onChange={(tab) => {
            const item = moduleTabs.find((t) => t.value === tab);
            if (item?.href) {
              navigate(item.href);
              return;
            }
            setModuleTab(tab);
          }}
          testId="accounts-module-tabs"
        />

        <div className="space-y-6 min-w-0">
          {moduleTab === "payments" ? (
            <AccountsCashLedgerPanel canIssue={canEditVoucher} onChanged={reloadAccounts} paymentMode="CASH" />
          ) : moduleTab === "receipts" ? (
            <AccountsCashLedgerPanel canIssue={canEditVoucher} onChanged={reloadAccounts} paymentMode="BANK" />
          ) : moduleTab === "labour" ? (
            <AccountsLabourSettlementPanel />
          ) : moduleTab === "sales" ? (
            <AccountsSalesSettlementPanel />
          ) : moduleTab !== "settlement" ? (
            renderModuleStub()
          ) : (
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab);
          if (tab === "pending") clearVoucherDeepLink();
        }}
        className="space-y-4"
      >
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="pending" className="text-xs sm:text-sm">
            Pending review ({pendingList.meta.count})
          </TabsTrigger>
          <TabsTrigger value="vouchers" className="text-xs sm:text-sm">
            Voucher register ({voucherList.meta.count})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <div
            className={cn(
              "grid grid-cols-1 gap-4 items-start min-w-0",
              // List rail capped at 320px; detail takes remaining width.
              "lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]",
            )}
          >
            <div className="min-w-0 w-full lg:max-w-80 lg:sticky lg:top-20 self-start">
              <SectionCard title="Awaiting review" testId="accounts-pending-queue" className="min-w-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    className="pl-8 h-8 text-xs w-full"
                    placeholder="Vehicle, GP, invoice, 9R, parchi, party, amount…"
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                  />
                </div>
                {(accountsAggregates?.pravesh_parchi_pending_count ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => setParchiPendingOnly((v) => !v)}
                    className={cn(
                      "mt-2 w-full rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
                      parchiPendingOnly
                        ? "border-amber-300 bg-amber-50 text-amber-900"
                        : "border-border bg-card hover:bg-muted/40 text-muted-foreground",
                    )}
                    data-testid="accounts-parchi-pending-filter"
                  >
                    {parchiPendingOnly ? "Showing supplier trucks missing pravesh parchi" : `Parchi pending · ${accountsAggregates.pravesh_parchi_pending_count} supplier truck(s)`}
                  </button>
                )}
                {pendingRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {pendingList.table.hasActiveFilters
                      ? "No pending purchases match your filters."
                      : "No purchases awaiting accounts."}
                  </p>
                ) : filteredPendingRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {pendingSearch.trim() ? "No matches for your search." : "No matches in this page."}
                  </p>
                ) : (
                  <div className="mt-3 space-y-1.5">
                    {filteredPendingRows.map((p) => (
                      <PurchaseQueueItem
                        key={p.id}
                        purchase={p}
                        selected={p.id === selectedPurchaseId}
                        onSelect={() => selectPendingPurchase(p.id)}
                      />
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2 min-w-0">
                  <CompactTableFilters
                    columns={PENDING_PURCHASE_COLUMNS}
                    table={pendingList.table}
                    testId="accounts-pending-filters"
                  />
                  <DataTablePagination
                    {...pendingList.pagination}
                    pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                    testId="accounts-pending-filters-pagination"
                    compact
                  />
                </div>
              </SectionCard>
            </div>

            <div className="min-w-0">
              <SectionCard
                title={selectedPurchase ? `Review · ${selectedPurchase.code}` : "Select a truck"}
                testId="accounts-pending"
                className="min-w-0"
              >
              {!selectedPurchaseId ? (
                <p className="text-sm text-muted-foreground py-12 text-center">
                  Select a truck from the list to link contracts, review purchase settlement, and finalize.
                </p>
              ) : selectedPurchaseLoading && !selectedPurchase ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading purchase…
                </div>
              ) : selectedPurchase ? (
                <PendingPurchaseReview
                  key={selectedPurchase.id}
                  purchase={selectedPurchase}
                  form={pruneFormForPurchase(selectedPurchase, selectedForm || formSeedForPurchase(selectedPurchase))}
                  canFinalize={canFinalize}
                  onUpdate={(field, value) => updateForm(selectedPurchase.id, field, value)}
                  onPatch={(patch) => patchForm(selectedPurchase.id, patch)}
                  onFinalize={() => finalize(selectedPurchase.id)}
                  onAllocSaved={refreshSelectedPurchase}
                  onDeleted={() => {
                    reloadAccounts();
                    setSelectedPurchaseId(null);
                    setSelectedPurchase(null);
                  }}
                  optionsFor={optionsFor}
                />
              ) : null}
              </SectionCard>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="vouchers">
          <div
            className={cn(
              "grid grid-cols-1 gap-4 items-start min-w-0",
              // List rail capped at 320px; detail workspace expands.
              "lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]",
            )}
          >
            <div className="min-w-0 w-full lg:max-w-80 lg:sticky lg:top-20 self-start">
              <SectionCard
                title="Voucher register"
                testId="vouchers-queue"
                className="min-w-0"
                action={(
                  <VoucherExportDialog
                    defaultFilters={{
                      code: voucherList.table.columnFilters.code || "",
                      purchase_code: voucherList.table.columnFilters.purchase_code || "",
                      party_name: voucherList.table.columnFilters.party_name || "",
                      variety: voucherList.table.columnFilters.variety || "",
                    }}
                    filename="vouchers.xlsx"
                    testId="vouchers-export"
                  />
                )}
              >
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    className="pl-8 h-8 text-xs w-full"
                    placeholder="Vehicle, GP, invoice, 9R, parchi, voucher, net payable…"
                    value={voucherSearch}
                    onChange={(e) => setVoucherSearch(e.target.value)}
                  />
                </div>
                {groupedVoucherRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {voucherList.table.hasActiveFilters
                      ? "No vouchers match your filters."
                      : "No vouchers yet. Finalize a purchase from the pending tab."}
                  </p>
                ) : (
                  <div className="mt-3 space-y-1.5">
                    {groupedVoucherRows.map((group) => (
                      <PurchaseVoucherQueueItem
                        key={group.key}
                        group={group}
                        selected={voucherGroupMatchesVoucherId(group, selectedVoucherId)}
                        onSelect={() => openVoucher(group.primaryId)}
                      />
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2 min-w-0">
                  <CompactTableFilters
                    columns={VOUCHER_COLUMNS}
                    table={voucherList.table}
                    testId="vouchers-filters"
                  />
                  <DataTablePagination
                    {...voucherList.pagination}
                    pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                    testId="vouchers-filters-pagination"
                    compact
                  />
                </div>
              </SectionCard>
            </div>

            <div className="min-w-0">
              <SectionCard
                title={
                  selectedVoucherGroup
                    ? `Purchase · ${selectedVoucherGroup.purchase_code || selectedVoucher?.code || "—"}`
                    : selectedVoucher
                      ? `Voucher · ${selectedVoucher.code}`
                      : "Select a voucher"
                }
                testId="vouchers-section"
                className="min-w-0"
                action={
                  canTallySync && selectedVoucher ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-bansal-sage text-primary"
                      onClick={syncSelectedVouchersToTally}
                      disabled={Boolean(
                        tallyDirectSyncBlockedReason(selectedVoucher)
                        || tallyDirectSyncBlockedReason(selectedVoucherGroup?.material)
                        || tallyDirectSyncBlockedReason(selectedVoucherGroup?.freight),
                      )}
                      title={
                        tallyDirectSyncBlockedReason(selectedVoucher)
                        || tallyDirectSyncBlockedReason(selectedVoucherGroup?.material)
                        || tallyDirectSyncBlockedReason(selectedVoucherGroup?.freight)
                        || undefined
                      }
                      data-testid="tally-sync-selected"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      Sync Selected
                    </Button>
                  ) : null
                }
              >
              {!selectedVoucher ? (
                <p className="text-sm text-muted-foreground py-12 text-center">
                  Select a purchase from the list — material and freight vouchers appear together per truck.
                </p>
              ) : (
                <VoucherDetailPanel
                  voucher={selectedVoucher}
                  voucherGroup={selectedVoucherGroup}
                  allVouchers={voucherRows}
                  canEdit={canEditVoucher}
                  canTallySync={canTallySync}
                  onSaved={() => {
                    reloadAccounts();
                    reloadAccounts();
                  }}
                  onDeleted={() => {
                    setSearchParams({}, { replace: true });
                    reloadAccounts();
                  }}
                />
              )}
              <TallySyncMappingDialog
                open={Boolean(bulkTallySyncTargets?.length)}
                onOpenChange={(open) => {
                  if (!open) setBulkTallySyncTargets(null);
                }}
                voucherIds={bulkTallySyncTargets || []}
                onSynced={() => reloadAccounts()}
              />
              </SectionCard>
            </div>
          </div>
        </TabsContent>
      </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountsModuleHeader({ pendingCount, voucherCount, totalPayable, outstanding, totalReceivable, praveshParchiPendingCount }) {
  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Accounts Workspace"
        title="Vouchers, Payments & Ledger"
        description="Accounts cockpit — purchase/sales/labour settlement and cash ledger. Full books sync to Tally Prime."
      />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        <SummaryStat label="Awaiting review" value={pendingCount ?? 0} tone="sage" />
        <SummaryStat label="Parchi pending" value={praveshParchiPendingCount ?? 0} tone="gold" />
        <SummaryStat label="Vouchers in register" value={voucherCount ?? 0} tone="ink" />
        <SummaryStat label="Total payable" value={`₹${(totalPayable || 0).toLocaleString("en-IN")}`} tone="sage" />
        <SummaryStat label="Outstanding payable" value={`₹${(outstanding || 0).toLocaleString("en-IN")}`} tone="gold" />
        <SummaryStat label="Total receivable" value={`₹${(totalReceivable || 0).toLocaleString("en-IN")}`} tone="ink" />
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tone = "ink" }) {
  const accents = {
    sage: "text-primary",
    gold: "text-bansal-goldDark",
    ink: "text-foreground",
  };
  return (
    <div className="relative bg-card border border-border rounded-2xl lg:rounded-lg p-4 sm:p-5 pwa-card overflow-hidden">
      <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-bansal-sage/70 to-bansal-gold/70" />
      <div className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground">{label}</div>
      <div className={`font-serif text-2xl sm:text-[1.65rem] mt-2 tabular leading-tight break-words ${accents[tone]}`}>
        {value}
      </div>
    </div>
  );
}

function accountsReopenSourceLabel(source) {
  const labels = {
    weighbridge: "Weighbridge",
    weigh_correction: "Weight correction",
    unloading: "Unloading",
    unloading_weights: "Unloading weights",
    gate_pass: "Gate pass",
    party_weight: "Party weight",
    contract_allocations: "Contract allocation",
    linked_contracts: "Linked contracts",
  };
  return labels[source] || "Purchase workflow";
}

function AccountsReopenBanner({ reopen }) {
  if (!reopen) return null;
  const when = reopen.at ? formatIndiaDateTime(reopen.at) : null;
  const roleLabel = reopen.actor_role || accountsReopenSourceLabel(reopen.source);
  const changes = Array.isArray(reopen.changes) ? reopen.changes.filter(Boolean) : [];
  return (
    <InfoNote tone="warning">
      <div className="flex gap-2 items-start">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1.5">
          <p className="font-semibold">Moved back for accounts review</p>
          <p>
            <span className="font-medium">{roleLabel}</span>{" "}
            <span className="font-medium">{reopen.by || "Someone"}</span> updated this truck
            {when ? ` on ${when}` : ""}. {reopen.reason || "Upstream purchase data changed after voucher finalization."}
          </p>
          {changes.length > 0 ? (
            <ul className="list-disc pl-4 space-y-0.5">
              {changes.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          <p>Please review settlement again and re-finalize the voucher.</p>
        </div>
      </div>
    </InfoNote>
  );
}

function PurchaseQueueItem({ purchase: p, selected, onSelect }) {
  const allocCount = p.contract_allocations?.length ?? 0;
  const partyStatus = accountsPartyWeightStatus(p);
  const splitStatus = accountsContractSplitStatus(p);
  const parchiStatus = accountsPraveshParchiStatus(p);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-md border px-3 py-2.5 transition-colors",
        selected
          ? "border-sky-400 bg-sky-50/80 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:ring-sky-800/60"
          : "border-border bg-card/70 text-muted-foreground hover:bg-muted/40 hover:text-foreground opacity-90 hover:opacity-100",
      )}
      data-testid={`accounts-queue-${p.code}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-mono text-xs font-semibold">{p.code}</p>
        <div className="flex flex-wrap justify-end gap-1">
          {p.accounts_reopen && <Pill tone="warning">Changed</Pill>}
          {parchiStatus.pending && <Pill tone="warning">Parchi pending</Pill>}
          {partyStatus.required && !partyStatus.ready && <Pill tone="warning">Party weight</Pill>}
          {partyStatus.ready && !splitStatus.ready && <Pill tone="warning">Split pending</Pill>}
        </div>
      </div>
      <p className="text-xs font-medium mt-1 truncate">{p.party_name}</p>
      {p.source_farmer_name ? (
        <p className="text-[10px] text-muted-foreground truncate">Farmer: {p.source_farmer_name}</p>
      ) : null}
      <PurchaseTraceLine purchase={p} />
      <p className="text-[10px] text-muted-foreground mt-0.5 tabular">
        {p.net_weight_qtl} qtl · {allocCount || "—"} contract{allocCount === 1 ? "" : "s"}
        {parchiStatus.value ? ` · Parchi ${parchiStatus.value}` : ""}
      </p>
    </button>
  );
}

const RELATED_CHIP_STYLES = {
  MATERIAL: "border-primary/35 bg-primary/15 text-primary",
  FREIGHT: "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200",
  TDS: "border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-200",
  CASH: "border-sky-500/30 bg-sky-500/10 text-sky-900 dark:text-sky-200",
  OTHER: "border-border bg-muted/50 text-muted-foreground",
};

function relatedChipStyle(part) {
  if (part?.type === "CASH" && part?.payment_mode === "BANK") {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200";
  }
  return RELATED_CHIP_STYLES[part?.type] || RELATED_CHIP_STYLES.OTHER;
}

function relatedChipPrefix(part) {
  if (part?.type === "MATERIAL") return "RM";
  if (part?.type === "FREIGHT") return "FR";
  if (part?.type === "TDS") return "TDS";
  if (part?.type === "CASH") return part?.payment_mode === "BANK" ? "BP" : "CSH";
  return (part?.type || "VC").slice(0, 3);
}

function PurchaseRelatedVoucherChips({ parts, className = "", dense = false }) {
  if (!parts?.length) return null;
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {parts.map((part) => (
        <span
          key={`${part.type}-${part.code}`}
          title={`${part.label}: ${part.code}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5",
            "text-[10px] font-mono leading-none whitespace-nowrap",
            relatedChipStyle(part),
          )}
        >
          <span className="font-sans font-semibold tracking-wide uppercase opacity-70 shrink-0">
            {relatedChipPrefix(part)}
          </span>
          {dense ? null : <span>{part.code}</span>}
        </span>
      ))}
    </div>
  );
}

function PurchaseVoucherAmountSummary({ material, freight, preview, size = "compact" }) {
  const summary = buildPurchaseVoucherAmountSummary({ material, freight, preview });
  const { materialBase, materialGross, partyFreight, transporterFreight } = summary;
  if (materialBase == null && !transporterFreight) return null;

  if (size === "compact") {
    return (
      <div className="mt-2 rounded-md border border-bansal-sage/20 bg-bansal-sageLight/25 px-2.5 py-1.5 space-y-0.5">
        {materialBase != null ? (
          <p className="text-[12px] font-semibold tabular text-foreground leading-snug">
            <span className="text-[9px] uppercase tracking-[0.12em] text-primary/90 font-medium mr-1.5">
              Material
            </span>
            {formatInr(materialBase)}
            {materialGross != null && materialGross !== materialBase ? (
              <span className="text-[10px] font-normal text-muted-foreground ml-1.5">
                gross {formatInr(materialGross)}
              </span>
            ) : null}
          </p>
        ) : null}
        {transporterFreight ? (
          <p className="text-[12px] font-semibold tabular text-foreground leading-snug">
            <span className="text-[9px] uppercase tracking-[0.12em] text-amber-800/90 font-medium mr-1.5">
              Freight{transporterFreight.draft ? " draft" : ""}
            </span>
            {formatInr(transporterFreight.amount)}
          </p>
        ) : null}
        {partyFreight ? (
          <p className="text-[10px] tabular text-muted-foreground leading-snug">
            {partyFreight.effect === "add" ? "+" : "−"}
            {formatInr(partyFreight.amount)} party freight
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="text-sm text-muted-foreground tabular space-y-0.5 mt-0.5">
      {materialBase != null ? (
        <p className="text-primary font-semibold">
          Material {formatInr(materialBase)}
          {materialGross ? (
            <span className="text-muted-foreground font-normal">
              {" "}(gross {formatInr(materialGross)})
            </span>
          ) : null}
        </p>
      ) : null}
      {partyFreight ? (
        <p>
          {partyFreight.effect === "add" ? "+" : "−"}
          {" "}{formatInr(partyFreight.amount)} {partyFreightDisplayLabel(partyFreight)}
        </p>
      ) : null}
      {transporterFreight ? (
        <p className="text-foreground font-semibold text-xs">
          Freight {formatInr(transporterFreight.amount)}
          {transporterFreight.draft ? " (draft)" : ""}
        </p>
      ) : null}
    </div>
  );
}

function PurchaseVoucherQueueItem({ group, selected, onSelect }) {
  const material = group.material;
  const freight = group.freight;
  const related = purchaseRegisterRelatedParts(group);
  const primary = material || freight || group.extras?.[0] || group.payments?.[0];
  if (!primary) return null;

  const parchiStatus = accountsVoucherPraveshParchiStatus(material || primary);
  const accountsReopen = group.accounts_reopen || material?.accounts_reopen || freight?.accounts_reopen;
  const settlementSaved = Boolean(
    material?.settlement_frozen
    || material?.purchase_state === "COMPLETED"
    || (freight && freight.freight_status === "FINALIZED"),
  );
  const freightDraft = Boolean(freight && freight.freight_status !== "FINALIZED");
  const tallyStatus = material?.tally_sync_status || freight?.tally_sync_status;
  const titleCode = group.purchase_code || primary.purchase_code || primary.code;
  const partyName = group.party_name || primary.party_name || "—";
  const vehicleNumber = String(group.vehicle_number || "").trim();
  const secondaryMeta = [
    group.gate_pass_code ? `GP ${group.gate_pass_code}` : null,
    group.contract_label || null,
    group.invoice_no ? `Inv ${group.invoice_no}` : null,
    group.form_9r_no ? `9R ${group.form_9r_no}` : null,
    parchiStatus.value ? `Parchi ${parchiStatus.value}` : null,
  ].filter(Boolean);

  const statusPills = [
    accountsReopen ? { key: "changed", label: "Changed" } : null,
    parchiStatus.pending ? { key: "parchi", label: "Parchi" } : null,
    !settlementSaved ? { key: "unsaved", label: "Unsaved" } : null,
    freightDraft ? { key: "freight", label: "Frt draft" } : null,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        "w-full rounded-lg border transition-colors duration-150 overflow-hidden",
        selected
          ? "border-sky-400 bg-sky-50/70 ring-1 ring-sky-200/80 dark:bg-sky-950/40 dark:ring-sky-800/60"
          : "border-border/80 bg-card/70 opacity-90 hover:opacity-100 hover:border-bansal-sage/40 hover:bg-muted/25",
      )}
      data-testid={`voucher-queue-${titleCode}`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="w-full text-left px-3 py-2.5 space-y-2"
      >
        {/* Identity — full-width rows so PT / party never fight badges */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[13px] font-semibold tracking-tight text-foreground whitespace-nowrap">
              {titleCode}
            </p>
            {tallyStatus ? (
              <TallySyncBadge status={tallyStatus} showDot={false} className="shrink-0 items-end" />
            ) : null}
          </div>
          <p className="text-[13px] font-semibold text-foreground break-words hyphens-none leading-snug">
            {partyName}
          </p>
          {vehicleNumber ? (
            <p className="font-mono text-[12px] font-medium tracking-wide text-foreground/85 whitespace-nowrap">
              {vehicleNumber}
            </p>
          ) : null}
        </div>

        {statusPills.length ? (
          <div className="flex flex-wrap gap-1">
            {statusPills.map((pill) => (
              <Pill key={pill.key} tone="warning">{pill.label}</Pill>
            ))}
          </div>
        ) : null}

        <PurchaseVoucherAmountSummary material={material} freight={freight} size="compact" />

        <PurchaseRelatedVoucherChips parts={related} dense className="pt-0.5" />

        {secondaryMeta.length ? (
          <p className="text-[10px] text-muted-foreground leading-snug break-words">
            {secondaryMeta.join(" · ")}
          </p>
        ) : null}
      </button>

      {(material || freight || (group.extras || []).length || (group.payments || []).length) ? (
        <PurchaseVoucherPrintBar group={group} compact />
      ) : null}
    </div>
  );
}

function VoucherDetailPanel({
  voucher: initialV,
  voucherGroup = null,
  allVouchers,
  canEdit,
  canTallySync = false,
  onSaved,
  onDeleted,
}) {
  const [v, setV] = useState(initialV);
  const [unlockEdit, setUnlockEdit] = useState(false);

  useEffect(() => {
    setV(initialV);
    setUnlockEdit(false);
  }, [initialV]);

  useEffect(() => {
    if (!initialV?.id) return undefined;
    let cancelled = false;
    api.get(`/vouchers/${initialV.id}`).then((r) => {
      if (!cancelled) setV(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [initialV?.id]);

  const settlementSaved = Boolean(
    v.settlement_frozen || v.deductions?.settlement_frozen || v.purchase_state === "COMPLETED",
  );
  const parchiStatus = accountsVoucherPraveshParchiStatus(v);
  const isDraft = v.voucher_type === "MATERIAL" && !settlementSaved;
  const tallyEligible = Boolean(
    v.tally_sync_eligible
    || (v.voucher_type === "MATERIAL" && settlementSaved)
    || (v.voucher_type === "FREIGHT" && v.freight_status === "FINALIZED")
    || (v.voucher_type === "TDS" && Number(v.net_payable || v.deductions?.tds_total || 0) > 0)
    || (["CASH", "RECEIPT", "SALES"].includes(v.voucher_type) && Number(v.net_payable || 0) > 0),
  );
  const tallySyncBlockedReason = tallyDirectSyncBlockedReason(v);
  const tallySoftLocked = Boolean(v.tally_soft_locked || v.tally_sync_status === "SYNCED") && !unlockEdit;
  const editAllowed = canEdit && !tallySoftLocked && v.state !== "CANCELLED";

  /** Material + Freight + TDS×2 for this purchase — posts all 4 Tally entries on Sync. */
  const purchaseSyncTargets = useMemo(() => {
    const targets = [];
    const push = (row) => {
      if (!row?.id) return;
      if (targets.some((t) => Number(t.id) === Number(row.id))) return;
      targets.push({ id: row.id, code: row.code || `#${row.id}` });
    };
    if (voucherGroup) {
      push(voucherGroup.material);
      push(voucherGroup.freight);
      for (const extra of voucherGroup.extras || []) push(extra);
      for (const payment of voucherGroup.payments || []) push(payment);
    }
    if (!targets.length && v?.purchase_id && Array.isArray(allVouchers)) {
      for (const row of allVouchers) {
        if (Number(row.purchase_id) !== Number(v.purchase_id)) continue;
        if (!["MATERIAL", "FREIGHT", "TDS", "CASH"].includes(row.voucher_type)) continue;
        push(row);
      }
    }
    if (!targets.length) push(v);
    return targets;
  }, [voucherGroup, allVouchers, v]);

  const [tallySyncTargets, setTallySyncTargets] = useState(null);
  const openTallySync = () => setTallySyncTargets(purchaseSyncTargets);

  const afterTallySynced = async () => {
    try {
      const { data } = await api.get(`/vouchers/${v.id}`);
      setV(data);
    } catch {
      /* list reload still runs via onSaved */
    }
    onSaved?.();
  };

  const previewTally = async () => {
    try {
      const chunks = [];
      for (const t of purchaseSyncTargets) {
        const { data } = await api.get(`/tally/vouchers/${t.id}/preview`);
        if (!data?.ok) {
          chunks.push(`${t.code}: ${(data?.errors || []).join("; ") || "failed"}`);
          continue;
        }
        const lines = (data?.lines || [])
          .map((ln) => `  ${ln.debit ? "Dr" : "Cr"} ${ln.tally_ledger_name}  ₹${Number(ln.debit || ln.credit || 0).toLocaleString("en-IN")}`)
          .join("\n");
        chunks.push(
          `${data?.voucher_code || t.code} · ${data?.voucher_type || ""}\n`
          + `Ref/narration: ${(data?.narration || "—").slice(0, 160)}\n`
          + `Dr ${Number(data?.debit_total || 0).toLocaleString("en-IN")} / Cr ${Number(data?.credit_total || 0).toLocaleString("en-IN")}\n`
          + (lines || "  —"),
        );
      }
      window.alert(`Tally preview · ${purchaseSyncTargets.length} entr${purchaseSyncTargets.length === 1 ? "y" : "ies"}\n\n${chunks.join("\n\n")}`);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const requestUnlockForEdit = () => {
    const ok = window.confirm(
      "This voucher is synced to Tally. Editing will mark it for re-sync (Alter).\n\nUnlock and continue?",
    );
    if (ok) setUnlockEdit(true);
  };

  const materialVoucher = voucherGroup?.material || (v.voucher_type === "MATERIAL" ? v : null);
  const freightVoucher = voucherGroup?.freight || (v.voucher_type === "FREIGHT" ? v : null);
  const relatedParts = purchaseRegisterRelatedParts(
    voucherGroup || {
      purchase_code: v.purchase_code,
      material: materialVoucher,
      freight: freightVoucher,
      extras: v.voucher_type === "TDS" ? [v] : [],
      payments: v.voucher_type === "CASH" ? [v] : [],
    },
  );
  const headerTitle = v.purchase_code || voucherGroup?.purchase_code || v.code;
  const contractLabel = materialVoucher?.contract_label || v.contract_label;
  const accountsReopen = voucherGroup?.accounts_reopen || v.accounts_reopen || materialVoucher?.accounts_reopen;

  return (
    <div className="space-y-4 min-w-0" data-testid={`voucher-${v.code}`}>
      <AccountsReopenBanner reopen={accountsReopen} />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl lg:rounded-lg border border-border bg-card px-4 py-3">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold tracking-tight text-foreground whitespace-nowrap truncate" title={headerTitle}>
            {headerTitle}
          </p>
          <p className="text-sm font-semibold text-foreground mt-0.5 truncate" title={v.party_name}>
            {v.party_name}
            {v.party_id ? (
              <>
                {" · "}
                <Link
                  to={`/parties/${v.party_id}/ledger`}
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  View ledger
                </Link>
              </>
            ) : null}
          </p>
          {contractLabel ? (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{contractLabel}</p>
          ) : null}
          <PurchaseRelatedVoucherChips parts={relatedParts} className="mt-2" />
          <div className="mt-1">
            <PurchaseVoucherAmountSummary
              material={materialVoucher}
              freight={freightVoucher}
              size="detail"
            />
          </div>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {parchiStatus.pending && <Pill tone="warning">Pravesh parchi pending</Pill>}
            {parchiStatus.value ? <Pill tone="default">Parchi {parchiStatus.value}</Pill> : null}
            {freightVoucher && freightVoucher.freight_status !== "FINALIZED" && <Pill tone="warning">Freight draft</Pill>}
            {v.voucher_type === "MATERIAL" && v.freight_settlement_waived && <Pill tone="muted">Freight waived</Pill>}
            {settlementSaved ? <Pill tone="success">Saved</Pill> : <Pill tone="warning">Unsaved</Pill>}
            <TallySyncBadge
              status={v.tally_sync_status}
              error={v.tally_error_message}
              approvalStatus={v.sync_approval_status}
              approvedBy={v.sync_approved_by_name || v.approved_by}
              approvedAt={v.sync_approved_at || v.approved_at}
              rejectionReason={v.sync_rejection_reason || v.rejection_reason}
            />
            {v.state === "CANCELLED" ? <Pill tone="danger">Cancelled</Pill> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
          {canTallySync && tallyEligible && v.state !== "CANCELLED" && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={previewTally}
                className="text-xs h-8"
                data-testid={`tally-preview-${v.code}`}
              >
                Preview Tally
              </Button>
              <span title={tallySyncBlockedReason || undefined} className="inline-flex">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openTallySync}
                  disabled={Boolean(tallySyncBlockedReason)}
                  className="text-xs h-8 border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                  data-testid={`tally-sync-now-${v.code}`}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Sync Now
                </Button>
              </span>
              {tallySyncBlockedReason ? (
                <Link to="/accounts/tally-approvals" className="text-[11px] underline text-muted-foreground">
                  Tally approvals
                </Link>
              ) : null}
            </>
          )}
          <AdminEntityActions
            entityType="voucher"
            entityId={v.id}
            entityLabel={v.code}
            onDeleted={onDeleted}
          />
        </div>
      </div>
      </div>

      {(() => {
        const printGroup = voucherGroup || (v.purchase_id && Array.isArray(allVouchers)
          ? {
              purchase_id: v.purchase_id,
              purchase_code: v.purchase_code,
              material: allVouchers.find((row) => Number(row.purchase_id) === Number(v.purchase_id) && row.voucher_type === "MATERIAL") || materialVoucher,
              freight: allVouchers.find((row) => Number(row.purchase_id) === Number(v.purchase_id) && row.voucher_type === "FREIGHT") || freightVoucher,
              extras: allVouchers.filter((row) => Number(row.purchase_id) === Number(v.purchase_id) && row.voucher_type === "TDS"),
              payments: allVouchers.filter((row) => Number(row.purchase_id) === Number(v.purchase_id) && row.voucher_type === "CASH"),
            }
          : null);
        if (printGroup) {
          return <PurchaseVoucherPrintBar group={printGroup} purchaseId={v.purchase_id || printGroup.purchase_id} />;
        }
        return (
          <div className="flex justify-end">
            <VoucherDocumentActions voucherId={v.id} voucherCode={v.code} label={v.voucher_type} />
          </div>
        );
      })()}

      <TallySyncMappingDialog
        open={Boolean(tallySyncTargets?.length)}
        onOpenChange={(open) => {
          if (!open) setTallySyncTargets(null);
        }}
        voucherIds={tallySyncTargets || []}
        onSynced={afterTallySynced}
      />

      {isDraft && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Settlement is not saved yet — click <strong>Save voucher</strong> below to lock amounts.
          {v.multi_contract ? " Each contract is listed separately; the total is one voucher." : ""}
        </p>
      )}
      {settlementSaved && v.voucher_type === "MATERIAL" && (
        <p className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          Settlement saved. Sync to Tally when ready — books are maintained only in Tally Prime.
        </p>
      )}
      {v.tally_sync_status === "RE_SYNC_REQUIRED" && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Voucher modified. Re-sync changes to Tally?
          {canTallySync ? (
            <Button type="button" size="sm" variant="outline" className="ml-2 h-7 text-xs" onClick={() => setTallySyncTargets(purchaseSyncTargets)}>
              Re-sync now
            </Button>
          ) : null}
        </p>
      )}
      {tallySoftLocked && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950 flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5" />
            Soft-locked — synced to Tally. Unlock to edit (will require Alter re-sync).
          </span>
          {canEdit ? (
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={requestUnlockForEdit}>
              Unlock to edit
            </Button>
          ) : null}
        </div>
      )}

      {(v.voucher_type === "MATERIAL" || v.voucher_type === "FREIGHT") && editAllowed && v.purchase_id ? (
        <VoucherSettlementEditPanel
          voucher={v}
          allVouchers={allVouchers}
          onSaved={() => {
            onSaved?.();
            api.get(`/vouchers/${v.id}`).then((r) => {
              setV(r.data);
              if (r.data?.tally_prompt) toast.message(r.data.tally_prompt);
            }).catch(() => {});
          }}
        />
      ) : (v.voucher_type === "MATERIAL" || v.voucher_type === "FREIGHT") && canEdit && tallySoftLocked ? (
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Fields are locked while this voucher is synced to Tally. Unlock above to edit.
          <div className="mt-3">
            <VoucherDocumentActions voucherId={v.id} voucherCode={v.code} />
          </div>
        </div>
      ) : (v.voucher_type === "MATERIAL" || v.voucher_type === "FREIGHT") && canEdit ? (
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {!v.purchase_id
            ? "No purchase is linked to this voucher."
            : v.state === "CANCELLED"
              ? "This voucher is cancelled."
              : "You do not have permission to edit vouchers."}
          <div className="mt-3">
            <VoucherDocumentActions voucherId={v.id} voucherCode={v.code} />
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          You do not have permission to edit vouchers.
          <div className="mt-3">
            <VoucherDocumentActions voucherId={v.id} voucherCode={v.code} />
          </div>
        </div>
      )}
    </div>
  );
}

function VoucherSettlementEditPanel({ voucher, allVouchers, onSaved }) {
  const { optionsFor } = useLookups();
  const [purchase, setPurchase] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const purchaseId = voucher.purchase_id;
  const siblingVouchers = useMemo(
    () => allVouchers.filter((row) => row.purchase_id === purchaseId),
    [allVouchers, purchaseId],
  );

  const loadPurchase = useCallback(async () => {
    if (!purchaseId) return null;
    const { data } = await api.get(`/purchases/${purchaseId}`);
    const vouchersForSeed = vouchersForEditSeed(data.vouchers, siblingVouchers, voucher);
    setPurchase(data);
    setForm(seedFormFromPurchaseForEdit(data, vouchersForSeed, voucher));
    return data;
  }, [purchaseId, siblingVouchers, voucher]);

  useEffect(() => {
    setForm(null);
    setPurchase(null);
  }, [voucher.id]);

  useEffect(() => {
    if (!purchaseId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    loadPurchase()
      .catch((err) => {
        if (!cancelled) toast.error(extractError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [purchaseId, voucher.id, loadPurchase]);

  const refreshPurchase = useCallback(async () => {
    await loadPurchase();
    onSaved?.();
  }, [loadPurchase, onSaved]);

  const purchaseCompleted = purchase?.state === "COMPLETED";

  const updateSettlement = async () => {
    if (!purchase || !form) return;
    try {
      // Draft / reopened purchases must finalize; only COMPLETED uses update-settlement.
      const endpoint = purchaseCompleted
        ? `/purchases/${purchaseId}/accounts-update-settlement`
        : `/purchases/${purchaseId}/accounts-finalize`;
      const { data } = await api.post(
        endpoint,
        buildFinalizePayload(purchase, form),
      );
      const vouchers = data?.vouchers || [];
      setPurchase(data);
      setForm(seedFormFromPurchaseForEdit(data, vouchers, voucher));
      const freight = vouchers.find((row) => row.voucher_type === "FREIGHT");
      const freightFinalized = freight?.freight_status === "FINALIZED"
        || freight?.deductions?.freight_status === "FINALIZED";
      if (form?.freight_waived) {
        toast.success(
          purchaseCompleted
            ? "Updated — freight waived (no freight voucher)"
            : "Voucher saved — freight waived (no freight voucher)",
        );
      } else if (!purchaseCompleted) {
        if (freight && freightFinalized) {
          toast.success(`Voucher saved — material + freight ${freight.code}`);
        } else if (freight) {
          toast.success(`Voucher saved — freight ${freight.code} is still a draft`);
        } else {
          toast.success("Voucher saved");
        }
      } else if (freight && freightFinalized) {
        toast.success(`Updated material + freight ${freight.code}`);
      } else if (freight) {
        toast.success(`Updated — freight ${freight.code} is still a draft`);
      } else {
        toast.success("Voucher updated");
      }
      await refreshPurchase();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  if (!purchaseId) {
    return (
      <p className="text-sm text-muted-foreground">No purchase is linked to this voucher.</p>
    );
  }
  if (loading || !purchase || !form) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settlement…
      </div>
    );
  }

  return (
    <PendingPurchaseReview
      key={`edit-${purchase.id}`}
      purchase={purchase}
      form={pruneFormForPurchase(purchase, form)}
      canFinalize
      editMode={purchaseCompleted}
      onUpdate={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
      onPatch={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
      onFinalize={updateSettlement}
      onAllocSaved={refreshPurchase}
      onDeleted={onSaved}
      optionsFor={optionsFor}
    />
  );
}

function PendingPurchaseReview({ purchase: p, form, canFinalize, editMode = false, onUpdate, onPatch, onFinalize, onAllocSaved, onDeleted, optionsFor }) {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [contractsDirty, setContractsDirty] = useState(false);
  const [draftPartyNetQtl, setDraftPartyNetQtl] = useState(null);
  const partyWeightRef = useRef(null);
  const contractsRef = useRef(null);
  const emandiRef = useRef(null);
  const [stepSaving, setStepSaving] = useState(false);
  const contractLinkKeyRef = useRef(null);
  const [assignSupplierOpen, setAssignSupplierOpen] = useState(false);

  const partyStatus = accountsPartyWeightStatus(p);
  const splitStatus = accountsContractSplitStatus(p);
  const parchiStatus = accountsPraveshParchiStatus(p);
  const refinalizeRequired = Boolean(p.accounts_refinalize_required || p.accounts_reopen);
  const allocRevision = purchaseAllocKey(p);
  const truckLoadFreight = purchaseRequiresFreightSettlement(p);
  const freightWaived = Boolean(form?.freight_waived);
  const freightApplicable = truckLoadFreight || Boolean(preview?.needs_freight_voucher);
  const freightReady = !freightApplicable || freightWaived || Boolean(preview?.freight_settlement);

  // -------- Step model --------
  const stepDefs = [
    {
      id: 1,
      label: "Party Side Weight",
      hint: partyStatus.required ? "Party weighbridge slip" : "Not required",
      icon: Scale,
      ready: partyStatus.ready,
      stepStatus: !partyStatus.required ? "not_required" : partyStatus.ready ? "completed" : "pending",
    },
    {
      id: 2,
      label: "Contract Linkage",
      hint: "Allocate & split net",
      icon: Link2,
      ready: splitStatus.ready && !contractsDirty,
      stepStatus: splitStatus.ready && !contractsDirty ? "completed" : "pending",
    },
    {
      id: 3,
      label: "Payment & e-Mandi",
      hint: "Bank, invoice, mandi forms",
      icon: Wallet,
      ready: splitStatus.ready,
      optional: true,
      stepStatus: splitStatus.ready ? "completed" : "pending",
    },
    {
      id: 4,
      label: "Material Voucher",
      hint: "Settlement review & finalize",
      icon: Receipt,
      ready: Boolean(preview) && !contractsDirty && splitStatus.ready,
      stepStatus: Boolean(preview) && !contractsDirty && splitStatus.ready ? "completed" : "pending",
    },
    {
      id: 5,
      label: "Freight",
      hint: freightWaived ? "Waived — no freight voucher" : freightApplicable ? "Truck load / FOR freight" : "Not required",
      icon: Truck,
      ready: freightReady && (Boolean(preview) || freightWaived || !freightApplicable),
      optional: !freightApplicable || freightWaived,
      stepStatus:
        !freightApplicable || freightWaived
          ? "not_required"
          : freightReady
            ? "completed"
            : "pending",
    },
    {
      id: 6,
      label: "TDS Review",
      hint: freightWaived || !freightApplicable
        ? "Material TDS vouchers"
        : "Material + freight TDS vouchers",
      icon: ShieldCheck,
      ready: Boolean(preview) && freightReady && splitStatus.ready && !contractsDirty,
      stepStatus:
        Boolean(preview) && freightReady && splitStatus.ready && !contractsDirty ? "completed" : "pending",
    },
  ];

  const completedSteps = stepDefs
    .filter((s) => s.stepStatus === "completed" || s.stepStatus === "not_required")
    .map((s) => s.id);
  const firstIncomplete = stepDefs.find((s) => s.stepStatus === "pending")?.id ?? 6;
  const [currentStep, setCurrentStep] = useState(firstIncomplete);

  useEffect(() => {
    setCurrentStep(firstIncomplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.id]);

  useEffect(() => {
    if (editMode) return undefined;
    // Reset form seeds when linkage changes. Do NOT clear preview here — if
    // previewParamsKey is unchanged the fetch effect would not re-run and
    // Material Voucher stays blank.
    onPatch({
      _terms_seeded: false,
      contract_terms: {},
      contract_terms_baseline: {},
      _freight_slip_seeded: false,
      freight_slip: {},
    });
    return undefined;
  }, [p.id, allocRevision, editMode]);

  useEffect(() => {
    if (contractLinkKeyRef.current != null && contractLinkKeyRef.current === allocRevision) return;
    const isInitial = contractLinkKeyRef.current == null;
    contractLinkKeyRef.current = allocRevision;
    if (isInitial) return;
    const { allocation_rates, revised_rate } = ratesFromPurchaseAllocations(p);
    const multi = (p.contract_allocations?.length ?? 0) > 1;
    const patch = { allocation_rates, _terms_seeded: false };
    if (!multi && revised_rate) patch.revised_rate = revised_rate;
    onPatch(patch);
  }, [allocRevision, p, onPatch]);

  useEffect(() => {
    if (!preview?.allocations?.length || form?._terms_seeded) return;
    const baseline = {};
    const terms = {};
    for (const alloc of preview.allocations) {
      const cid = String(alloc.purchase_contract_id);
      const orig = pickContractTermsFields(alloc.original_contract_terms || alloc.contract_terms || {});
      baseline[cid] = { ...orig };
      terms[cid] = { ...orig };
    }
    onPatch({ contract_terms_baseline: baseline, contract_terms: terms, _terms_seeded: true });
  }, [preview, allocRevision, form?._terms_seeded]);

  const previewParamsKey = useMemo(() => {
    if (!form || !partyStatus.ready || !splitStatus.ready) return null;
    return JSON.stringify({
      ...buildSettlementPreviewParams(p, form),
      // Force refetch when contract linkage changes even if rates/cuts are unchanged.
      _alloc: allocRevision,
    });
  }, [p, form, partyStatus.ready, splitStatus.ready, allocRevision]);

  useEffect(() => {
    if (!previewParamsKey || !canFinalize) {
      if (!partyStatus.ready || !splitStatus.ready) {
        setPreview(null);
        setPreviewError(null);
        setLoadingPreview(false);
      }
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setLoadingPreview(true);
      setPreviewError(null);
      api
        .get(`/purchases/${p.id}/accounts-settlement-preview`, {
          params: (() => {
            const parsed = JSON.parse(previewParamsKey);
            delete parsed._alloc;
            return parsed;
          })(),
          skipDedupe: true,
        })
        .then((r) => {
          if (!cancelled) {
            setPreview(r.data);
            setPreviewError(null);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(extractError(err));
        })
        .finally(() => {
          if (!cancelled) setLoadingPreview(false);
        });
    }, currentStep === 4 ? 0 : 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
      // If a newer effect supersedes this one, don't leave the spinner stuck on.
      setLoadingPreview(false);
    };
  }, [previewParamsKey, canFinalize, p.id, partyStatus.ready, splitStatus.ready, previewNonce, currentStep]);

  // Seed / refresh TDS review lines from preview; preserve manual edits.
  useEffect(() => {
    const tds = preview?.tds_review;
    if (!tds) return;
    const autoRows = [
      ...(tds.material?.rows || []),
      ...(tds.freight?.rows || []),
    ];
    if (!autoRows.length) return;
    const prevByKey = Object.fromEntries(
      (form?.tds_review_lines || []).map((ln) => [ln.line_key, ln]),
    );
    const next = autoRows.map((r) => {
      const prev = prevByKey[r.line_key];
      if (prev?.manual) {
        return {
          ...r,
          ...prev,
          gross_amount: r.gross_amount,
          label: r.label || prev.label,
          payable_code: r.payable_code || prev.payable_code,
          scope: r.scope || prev.scope,
          line_kind: r.line_kind || prev.line_kind,
          manual: true,
        };
      }
      return {
        line_key: r.line_key,
        scope: r.scope || "MATERIAL",
        line_kind: r.line_kind,
        nature_code: r.nature_code || "NONE",
        payment_code: r.payment_code || "",
        label: r.label || "",
        payable_code: r.payable_code || "",
        gross_amount: r.gross_amount,
        taxable_amount: r.taxable_amount,
        tds_pct: r.tds_pct,
        tds_amount: r.tds_amount,
        tds_applicable: r.tds_applicable,
        reason: r.reason || "",
        manual: false,
      };
    });
    const prevJson = JSON.stringify(form?.tds_review_lines || []);
    const nextJson = JSON.stringify(next);
    if (prevJson !== nextJson) {
      onPatch({ tds_review_lines: next });
    }
  }, [preview?.tds_review]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchTdsReviewLine = (lineKey, partial) => {
    const rows = [...(form?.tds_review_lines || [])];
    const idx = rows.findIndex((r) => r.line_key === lineKey);
    if (idx < 0) return;
    const natureOptions = preview?.tds_review?.nature_options || [];
    let next = { ...rows[idx], ...partial, manual: true };
    if (partial.nature_code != null) {
      const opt = natureOptions.find((o) => o.value === partial.nature_code);
      next.payment_code = opt?.payment_codes || (partial.nature_code === "NONE" ? "" : next.payment_code);
      if (partial.nature_code === "NONE") {
        next.tds_amount = 0;
        next.tds_pct = 0;
        next.tds_applicable = false;
      } else {
        next.tds_applicable = Number(next.tds_amount) > 0;
      }
    }
    if (partial.tds_amount != null) {
      const amt = Number(partial.tds_amount);
      next.tds_amount = Number.isFinite(amt) ? amt : 0;
      next.tds_applicable = next.nature_code !== "NONE" && next.tds_amount > 0;
      const gross = Number(next.gross_amount) || 0;
      if (gross > 0 && next.tds_amount > 0) {
        next.tds_pct = Math.round((next.tds_amount * 10000) / gross) / 100;
      }
    }
    rows[idx] = next;
    onPatch({ tds_review_lines: rows });
  };

  const multiContract = (p.contract_allocations?.length ?? 0) > 1;

  const previewAmountSummary = preview
    ? buildPurchaseVoucherAmountSummary({
        preview: freightWaived
          ? { ...preview, freight_settlement: null }
          : preview,
      })
    : null;

  const updateContractTerms = (contractId, patch) => {
    const cid = String(contractId);
    onUpdate("contract_terms", {
      ...(form?.contract_terms || {}),
      [cid]: { ...(form?.contract_terms?.[cid] || {}), ...patch },
    });
  };

  const updateAllocationRate = (contractId, value) => {
    onUpdate("allocation_rates", {
      ...(form?.allocation_rates || {}),
      [String(contractId)]: value,
    });
  };

  // Navigation — Next saves the current step, then advances.
  const goPrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const refreshPurchase = useCallback(async () => {
    await Promise.resolve(onAllocSaved?.());
  }, [onAllocSaved]);

  const goNext = async () => {
    if (currentStep >= stepDefs.length || stepSaving) return;
    setStepSaving(true);
    try {
      let ok = true;
      switch (currentStep) {
        case 1:
          if (partyStatus.required) {
            const saveFn = partyWeightRef.current?.save;
            if (!saveFn) {
              ok = false;
              toast.error("Party weight form is not ready — try again");
            } else {
              ok = (await saveFn()) !== false;
            }
          }
          break;
        case 2:
          if (!partyStatus.ready) {
            ok = false;
          } else {
            const saveFn = contractsRef.current?.save;
            if (!saveFn) {
              ok = false;
              toast.error("Contract form is not ready — try again");
            } else {
              // Editor.save already refreshes the purchase when it writes.
              ok = (await saveFn()) !== false;
            }
          }
          break;
        case 3:
          if (!splitStatus.ready) {
            ok = false;
          } else {
            const saveFn = emandiRef.current?.save;
            if (saveFn) {
              ok = (await saveFn({ optional: true })) !== false;
            }
          }
          break;
        default:
          ok = true;
      }
      if (!ok) return;
      setCurrentStep((s) => Math.min(s + 1, stepDefs.length));
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setStepSaving(false);
    }
  };

  // ---------- Render step content ----------
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepFrame title="Party Side Weight" icon={Scale} description={partyStatus.required ? ((p.contract_allocations || []).some((a) => String(a.delivery_condition || "").toUpperCase() === "FOR") ? "Enter the party weighbridge weight. Material weight is the lower of this and our weighbridge." : "Enter party weighbridge weight, then click Next.") : "Not required for this truck — click Next to continue."} testId="settlement-step-party">
            {partyStatus.required ? (
              <AccountsPartyWeightEditor
                ref={partyWeightRef}
                purchase={p}
                canEdit={canFinalize}
                onSaved={refreshPurchase}
                onDraftQtlChange={setDraftPartyNetQtl}
              />
            ) : (
              <EmptyState
                icon={Scale}
                title="No party weight needed"
                description="Allocated contracts are not truck-load type. Click Next to continue to contract linkage."
              />
            )}
          </StepFrame>
        );
      case 2:
        return (
          <StepFrame title="Contract Linkage" icon={Link2} description="Allocate net weight across contracts, then click Next to save and continue." testId="settlement-step-contracts">
            {!partyStatus.ready ? (
              <InfoNote tone="warning">Complete party side weight first (Step 1).</InfoNote>
            ) : (
              <>
                <AccountsLinkedContractsEditor
                  ref={contractsRef}
                  purchase={p}
                  canEdit={canFinalize}
                  onSaved={refreshPurchase}
                  onDirtyChange={setContractsDirty}
                />
                {contractsDirty && (
                  <InfoNote tone="info">Unsaved contract changes — Next will save them before continuing.</InfoNote>
                )}
              </>
            )}
          </StepFrame>
        );
      case 3:
        return (
          <StepFrame title="Payment & e-Mandi" icon={Wallet} description="Bank and mandi document numbers (optional). Click Next to save if entered, or skip ahead." testId="settlement-step-payment">
            {!splitStatus.ready ? (
              <InfoNote tone="warning">Complete contract linkage first (Step 2).</InfoNote>
            ) : (
              <AccountsEmandiSection ref={emandiRef} purchase={p} form={form} onPatch={onPatch} canEdit={canFinalize} onSaved={refreshPurchase} />
            )}
          </StepFrame>
        );
      case 4:
        return (
          <StepFrame
            title="Material Voucher"
            icon={Receipt}
            description="Review per-contract settlement and party expenses. Save the voucher from the last step (or below) when done."
            testId="settlement-step-finalize"
          >
            {!splitStatus.ready || contractsDirty ? (
              <InfoNote tone="warning">
                {!splitStatus.ready
                  ? splitStatus.message
                  : "Save linked contract changes before finalizing."}
              </InfoNote>
            ) : (
              <>
                {loadingPreview && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2" data-testid="settlement-preview-loading">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Calculating settlement…
                  </div>
                )}
                {previewError && (
                  <InfoNote tone="danger">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>Settlement preview failed: {previewError}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setPreviewNonce((n) => n + 1)}
                      >
                        Retry
                      </Button>
                    </div>
                  </InfoNote>
                )}
                {!loadingPreview && !previewError && !preview && (
                  <InfoNote tone="warning">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>Settlement calculation has not loaded yet.</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setPreviewNonce((n) => n + 1)}
                        data-testid="settlement-preview-retry"
                      >
                        Load calculation
                      </Button>
                    </div>
                  </InfoNote>
                )}
                {preview && (
                  <div className="space-y-4">
                    <SettlementReviewTable
                      title="Truck-level (material)"
                      rows={buildTruckReviewRows(form, { isMandi: Boolean(p.is_mandi_purchase), scope: "material" })}
                      form={form}
                      onUpdate={onUpdate}
                      optionsFor={optionsFor}
                    />
                    {preview.allocations?.map((alloc) => (
                      <SettlementReviewTable
                        key={alloc.allocation_id ?? alloc.purchase_contract_id}
                        title={`${alloc.contract_number || alloc.purchase_contract_id}${alloc.variety ? ` · ${alloc.variety}` : ""}`}
                        subtitle={`${alloc.net_qtl} qtl net · ${labelFromOptions(optionsFor("delivery_condition"), form?.contract_terms?.[String(alloc.purchase_contract_id)]?.delivery_condition || alloc.contract_terms?.delivery_condition)}`}
                        rows={buildSettlementReviewRows(alloc, form, optionsFor, {
                          multiContract,
                          isMandi: Boolean(p.is_mandi_purchase || alloc.contract_terms?.purchase_type === "MANDI_PURCHASE"),
                        })}
                        form={form}
                        onUpdate={onUpdate}
                        onUpdateAllocationRate={updateAllocationRate}
                        onUpdateContractTerms={updateContractTerms}
                        optionsFor={optionsFor}
                      />
                    ))}
                    <SupplierLandedCostCard
                      preview={preview}
                      allocation={preview.allocations?.[0] || preview.allocation}
                    />
                    <PartyExpenseEditor
                      preview={preview}
                      form={form}
                      mode="purchase"
                      billableQtl={Number(p.net_weight_qtl || preview?.billable_qtl || 0)}
                      grossAmount={Number(
                        preview?.gross_amount
                        ?? ((p.negotiated_rate || p.rate_per_qtl || 0) * (p.net_weight_qtl || 0))
                      )}
                      frozen={editMode || Boolean(form?._settlement_frozen || form?._party_expense_frozen)}
                      source={
                        form?.party_expense_manual || form?._party_expense_frozen
                          ? "manual"
                          : editMode
                            ? "frozen"
                            : "live"
                      }
                      onChange={(patch) => onPatch(patch)}
                      onReset={() => onPatch({
                        party_expense_manual: false,
                        party_expense_lines: [],
                        clear_party_expense_overrides: true,
                        _party_expense_frozen: false,
                      })}
                    />
                  </div>
                )}
              </>
            )}
          </StepFrame>
        );
      case 5:
        return (
          <StepFrame
            title="Freight Voucher"
            icon={Truck}
            description="Freight rate, transporter slip, and payment mode for truck-load or FOR delivery — saved with the material voucher. FOR: amounts we pay are deducted from the supplier bill."
            testId="settlement-step-freight"
          >
            {!preview ? (
              <InfoNote tone="warning">Complete material settlement review first.</InfoNote>
            ) : !freightApplicable ? (
              <EmptyState
                icon={Truck}
                title="No freight settlement for this purchase"
                description="Linked contracts are not truck load or FOR delivery. Finalize from the material step only."
              />
            ) : freightWaived ? (
              <InfoNote tone="info">
                Freight settlement is <strong>not required</strong> — no freight voucher will be created. Check “Freight settlement required” to enter freight details.
              </InfoNote>
            ) : null}
            {freightApplicable && preview && !freightWaived && preview.freight_notice && (
              <InfoNote tone="warning">{preview.freight_notice}</InfoNote>
            )}
            {freightApplicable && preview && (
              <PendingFreightSettlementEditor
                purchase={p}
                form={form}
                basePreview={preview}
                canEdit={canFinalize}
                onUpdate={onUpdate}
                onPatch={onPatch}
                optionsFor={optionsFor}
                isMandi={Boolean(p.is_mandi_purchase)}
              />
            )}
          </StepFrame>
        );
      case 6: {
        const tds = preview?.tds_review;
        const mat = tds?.material;
        const frt = tds?.freight;
        const natureOptions = tds?.nature_options || DEFAULT_TDS_NATURE_OPTIONS;
        const edited = form?.tds_review_lines || [];
        const mergeRows = (rows = []) => rows.map((r) => {
          const e = edited.find((x) => x.line_key === r.line_key);
          return e ? { ...r, ...e } : r;
        });
        const matRows = mergeRows(mat?.rows || []);
        const frtRows = mergeRows(frt?.rows || []);
        const matTotal = matRows.reduce((s, r) => s + (r.tds_applicable ? Number(r.tds_amount) || 0 : 0), 0);
        const frtTotal = frtRows.reduce((s, r) => s + (r.tds_applicable ? Number(r.tds_amount) || 0 : 0), 0);
        const slip = form?.freight_slip || {};
        const freightPayee = String(slip.freight_payee || "").toUpperCase();
        const planned = Number(tds?.vouchers_planned || 0);
        return (
          <StepFrame
            title="TDS Review"
            icon={ShieldCheck}
            description="Review and edit TDS on each purchase / expense line and freight. Saving creates Material + Freight vouchers, then TDS vouchers linked to each."
            testId="settlement-step-tds"
          >
            {!preview ? (
              <InfoNote tone="warning">Complete earlier steps so settlement preview (and TDS) can load.</InfoNote>
            ) : (
              <div className="space-y-4">
                <InfoNote tone="info">
                  On save you get up to <strong>4 vouchers</strong>: Material, Freight
                  {freightWaived || !freightApplicable ? " (skipped if waived/N/A)" : ""}, TDS on material, and TDS on freight.
                  {" "}Planned TDS vouchers: <strong>{planned}</strong>
                  {" · "}Total TDS{" "}
                  <strong className="tabular">{formatInr(matTotal + frtTotal)}</strong>
                </InfoNote>

                <TdsReviewEditableBlock
                  title={mat?.title || "TDS on material / purchase"}
                  partyName={mat?.party_name || p.party_name}
                  rows={matRows}
                  totalTds={matTotal}
                  natureOptions={natureOptions}
                  canEdit={canFinalize}
                  emptyHint="No material / expense lines for TDS."
                  onPatchLine={patchTdsReviewLine}
                />

                {freightWaived || !freightApplicable ? (
                  <InfoNote tone="info">Freight not applicable / waived — no freight TDS voucher.</InfoNote>
                ) : (
                  <div className="space-y-3">
                    <FreightTdsFields
                      value={slip}
                      disabled={!canFinalize}
                      settlement={preview?.freight_settlement}
                      onChange={(next) => {
                        const merged = { ...slip, ...next };
                        const kind = String(merged.freight_payee || freightPayee || "").toUpperCase();
                        const pan = (merged.pan_no || "").trim().toUpperCase();
                        if (kind === "TRUCK_OWNER") merged.truck_owner_pan = pan;
                        if (kind === "TRANSPORT_COMPANY") merged.transport_company_pan = pan;
                        if (kind === "TRANSPORTER") merged.transporter_pan = pan;
                        if (kind === "PARTY") merged.party_pan = pan;
                        merged.pan_no = resolveFreightTdsPan(merged, p);
                        onPatch({ freight_slip: merged });
                      }}
                      payeeLabel={FREIGHT_PAYEE_OPTIONS.find((o) => o.value === freightPayee)?.label}
                    />
                    <TdsReviewEditableBlock
                      title={frt?.title || "TDS on freight"}
                      partyName={frt?.party_name || "Transporter"}
                      rows={frtRows}
                      totalTds={frtTotal}
                      natureOptions={natureOptions}
                      canEdit={canFinalize}
                      emptyHint="No freight TDS (below threshold or exempt)."
                      onPatchLine={patchTdsReviewLine}
                    />
                  </div>
                )}

                {tds?.buyer_1031_enabled === false ? (
                  <InfoNote tone="warning">
                    Buyer 1031 gate is OFF (`TDS_1031_ENABLED`) — purchase-of-goods TDS will not apply unless you set a section manually.
                  </InfoNote>
                ) : null}
              </div>
            )}
          </StepFrame>
        );
      }
      default:
        return null;
    }
  };

  const manualCharges = form?.party_expense_manual ? (form?.party_expense_lines || []) : [];
  const chargesTotal = manualCharges.reduce((sum, ln) => sum + (Number(ln.amount) || 0), 0);

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0" data-testid={`accounts-pending-${p.code}`}>
      {/* Purchase header — full-width grid so party / contracts / weighment share the row */}
      <div className="min-w-0 rounded-xl lg:rounded-md border border-border bg-muted/35 p-3 sm:p-4 space-y-3">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
            <div className="min-w-0 flex-1 basis-[12rem]">
              <Link
                to={`/purchases/${p.id}`}
                className="font-mono text-sm font-semibold link-brand whitespace-nowrap"
              >
                {p.code}
              </Link>
              <PurchaseTraceLine purchase={p} className="mt-0.5" />
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <StatePill state={p.state} />
              {parchiStatus.pending && <Pill tone="warning">Pravesh parchi pending</Pill>}
              {parchiStatus.value ? <Pill tone="default">Parchi {parchiStatus.value}</Pill> : null}
              {!editMode && p.state === "ACCOUNTS_PENDING" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                  onClick={() => setAssignSupplierOpen(true)}
                  data-testid="accounts-assign-supplier"
                >
                  <Users className="h-3.5 w-3.5 mr-1" />
                  {p.party_kind === "SUPPLIER" ? "Change supplier / charges" : "Assign to supplier"}
                </Button>
              )}
              <AdminEntityActions
                entityType="purchase"
                entityId={p.id}
                entityLabel={p.code}
                editTo={`/purchases/${p.id}`}
                onDeleted={onDeleted}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 min-w-0">
          <div className="min-w-0 rounded-md border border-border/60 bg-card/60 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Party</p>
            <p className="text-sm font-medium mt-1 break-words leading-snug">{p.party_name || "—"}</p>
            {p.party_kind ? (
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{p.party_kind}</p>
            ) : null}
            {p.party_id ? (
              <Button size="sm" variant="link" className="h-auto p-0 mt-1.5 text-xs" asChild>
                <Link to={`/parties/${p.party_id}/ledger`} data-testid="accounts-view-party-ledger">
                  View ledger
                </Link>
              </Button>
            ) : null}
            {p.source_farmer_name ? (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="accounts-source-farmer">
                Gate farmer: <span className="font-medium text-foreground">{p.source_farmer_name}</span>
              </p>
            ) : null}
          </div>

          <div className="min-w-0 rounded-md border border-border/60 bg-card/60 px-3 py-2.5 sm:col-span-1 xl:col-span-1">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Contract</p>
            <ContractAllocationChips purchase={p} dense />
          </div>

          <div className="min-w-0 rounded-md border border-border/60 bg-card/60 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Weighment</p>
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs tabular">
              <div>
                <p className="text-[10px] text-muted-foreground">Gross</p>
                <p className="font-medium">{p.gross_weight_qtl != null ? `${p.gross_weight_qtl} qtl` : "—"}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Tare</p>
                <p className="font-medium">{p.tare_weight_qtl != null ? `${p.tare_weight_qtl} qtl` : "—"}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Net</p>
                <p className="font-medium">{p.net_weight_qtl != null ? `${p.net_weight_qtl} qtl` : "—"}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Bags</p>
                <p className="font-medium">{p.bags_reported ?? "—"}</p>
              </div>
            </div>
          </div>

          <div className="min-w-0 rounded-md border border-border/60 bg-card/60 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Supplier charges</p>
            {manualCharges.length > 0 ? (
              <div className="mt-1.5 space-y-1">
                {manualCharges.slice(0, 3).map((ln, idx) => (
                  <div key={`${ln.code}-${idx}`} className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="truncate text-muted-foreground">{ln.label || ln.code}</span>
                    <span className="tabular font-medium shrink-0">
                      {ln.basis === "GROSS_AMOUNT_PCT" && ln.rate != null
                        ? `${Number(ln.rate)}%`
                        : ln.basis === "FIXED_PER_QTL" && ln.rate != null
                          ? `₹${Number(ln.rate)}/qtl`
                          : formatInr(ln.amount)}
                    </span>
                  </div>
                ))}
                {manualCharges.length > 3 ? (
                  <p className="text-[10px] text-muted-foreground">+{manualCharges.length - 3} more</p>
                ) : null}
                <p className="text-xs font-semibold tabular pt-1 border-t border-border/50">
                  Est. {formatInr(chargesTotal)}
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                {p.party_kind === "SUPPLIER"
                  ? "No manual charges set — use Change supplier / charges."
                  : "Assign a supplier to add commission and other charges."}
              </p>
            )}
          </div>
        </div>
      </div>

      <AssignSupplierDialog
        open={assignSupplierOpen}
        onOpenChange={setAssignSupplierOpen}
        purchase={p}
        existingExpenseLines={form?.party_expense_manual ? (form?.party_expense_lines || null) : null}
        onAssigned={({ purchase: updated, party_expense_lines }) => {
          onPatch({
            party_expense_lines: party_expense_lines || [],
            party_expense_manual: true,
            clear_party_expense_overrides: false,
          });
          onAllocSaved?.(updated);
          setCurrentStep(4);
          toast.success(
            p.party_kind === "SUPPLIER"
              ? "Supplier / charges updated — review on Material Voucher"
              : "Supplier assigned — review commission charges on Material Voucher",
          );
        }}
      />

      <AccountsReopenBanner reopen={p.accounts_reopen} />

      <TruckWeightSummary
        purchase={p}
        draftPartyNetQtl={draftPartyNetQtl}
        formPartyNetQtl={editMode ? form?.party_net_weight_qtl : undefined}
      />

      {/* Stepper */}
      <SettlementStepper
        steps={stepDefs.map((s) => ({
          id: s.id,
          label: s.label,
          hint: s.hint,
          optional: s.optional,
          status: s.stepStatus,
        }))}
        currentStep={currentStep}
        completed={completedSteps}
        onSelect={(id) => setCurrentStep(id)}
        testId="settlement-stepper"
      />

      {/* Step content */}
      <div className="min-w-0">{renderStep()}</div>

      {/* Step navigation + finalize action */}
      <div className="space-y-3 pt-3 border-t border-border/60 mt-2">
        {(() => {
          if (!partyStatus.ready) {
            return (
              <SettlementFooterNotice step={1} message="Enter party side weight to continue." />
            );
          }
          if (!splitStatus.ready) {
            return <SettlementFooterNotice step={2} message={splitStatus.message} />;
          }
          if (contractsDirty) {
            return <SettlementFooterNotice step={2} message="Click Next to save linked contracts." />;
          }
          if (loadingPreview) {
            return <p className="text-xs text-muted-foreground">Loading settlement preview…</p>;
          }
          if (preview) {
            return (
              <div className="text-xs text-muted-foreground leading-relaxed">
                <span className="inline-flex flex-col gap-0.5 sm:inline-flex sm:flex-row sm:flex-wrap sm:gap-x-4 sm:gap-y-1">
                  {previewAmountSummary?.materialBase != null ? (
                    <span>
                      Material{" "}
                      <span className="font-semibold text-primary tabular">
                        ₹{previewAmountSummary.materialBase.toLocaleString("en-IN")}
                      </span>
                    </span>
                  ) : null}
                  {previewAmountSummary?.partyFreight ? (
                    <span className="tabular">
                      {previewAmountSummary.partyFreight.effect === "add" ? "+" : "−"}
                      {" "}₹{previewAmountSummary.partyFreight.amount.toLocaleString("en-IN")}
                      {" "}{partyFreightDisplayLabel(previewAmountSummary.partyFreight)}
                    </span>
                  ) : null}
                  {freightWaived ? (
                    <span className="text-muted-foreground">Freight settlement waived</span>
                  ) : freightApplicable && previewAmountSummary?.transporterFreight ? (
                    <span>
                      Freight{" "}
                      <span className="font-semibold text-foreground tabular">
                        ₹{previewAmountSummary.transporterFreight.amount.toLocaleString("en-IN")}
                      </span>
                      {form?.freight_finalize !== false ? " (finalize together)" : ""}
                    </span>
                  ) : null}
                  {currentStep === 6 && preview?.tds_review ? (
                    <span>
                      TDS{" "}
                      <span className="font-semibold tabular">
                        ₹{Number(preview.tds_review.total_tds || 0).toLocaleString("en-IN")}
                      </span>
                      {" · "}
                      {preview.tds_review.vouchers_planned || 0} TDS voucher(s)
                    </span>
                  ) : null}
                </span>
              </div>
            );
          }
          return (
            <p className="text-xs text-muted-foreground">
              Review each step, then save vouchers on step 6 (TDS).
            </p>
          );
        })()}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={currentStep === 1}
              data-testid="settlement-step-prev"
              className="min-h-10 flex-1 sm:flex-none"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button
              type="button"
              variant={currentStep < stepDefs.length ? "default" : "outline"}
              size="sm"
              onClick={goNext}
              disabled={currentStep === stepDefs.length || stepSaving}
              data-testid="settlement-step-next"
              className={`min-h-10 flex-1 sm:flex-none ${currentStep < stepDefs.length ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}`}
            >
              {stepSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving…</>
              ) : (
                <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </div>

          {canFinalize && (
            <Button
              type="button"
              className="bg-bansal-gold hover:bg-bansal-goldDark text-white min-h-10 w-full sm:w-auto sm:max-w-sm disabled:opacity-60"
              onClick={onFinalize}
              disabled={
                currentStep < 6
                || !partyStatus.ready
                || !splitStatus.ready
                || contractsDirty
                || !preview
                || loadingPreview
                || (freightApplicable && !freightWaived && !freightReady)
              }
              data-testid={editMode ? `accounts-update-${p.code}` : `accounts-finalize-${p.code}`}
            >
              <FileCheck2 className="h-4 w-4 mr-1.5 shrink-0" />
              <span className="truncate">
                {editMode
                  ? "Update vouchers (incl. TDS)"
                  : refinalizeRequired
                    ? (freightWaived || !freightApplicable
                      ? "Re-finalize — Material & TDS"
                      : "Re-finalize — Material, Freight & TDS")
                    : (freightWaived || !freightApplicable
                      ? "Save vouchers — Material & TDS"
                      : "Save vouchers — Material, Freight & TDS")}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const DEFAULT_TDS_NATURE_OPTIONS = [
  { value: "NONE", label: "No TDS", payment_codes: "" },
  { value: "PURCHASE_1031", label: "Purchase of goods — 1031", payment_codes: "1031" },
  { value: "COMMISSION_1006", label: "Commission / brokerage — 1006", payment_codes: "1006" },
  { value: "FREIGHT_1023", label: "Freight / transport — 1023 / 1024", payment_codes: "1023/1024" },
  { value: "CONTRACTOR_1023", label: "Contractor / labour / loading — 1023 / 1024", payment_codes: "1023/1024" },
];

function SettlementFooterNotice({ step, message }) {
  return (
    <div
      className="rounded-lg border border-amber-500/35 bg-amber-500/8 px-3 py-2.5 text-sm text-amber-950 dark:text-amber-100"
      role="status"
      data-testid="settlement-footer-notice"
    >
      <span className="font-medium">Step {step}</span>
      <span className="text-amber-900/85 dark:text-amber-100/90"> — {message}</span>
    </div>
  );
}

function TdsReviewEditableBlock({
  title,
  partyName,
  rows,
  totalTds,
  natureOptions = DEFAULT_TDS_NATURE_OPTIONS,
  canEdit = false,
  emptyHint,
  onPatchLine,
}) {
  return (
    <div className="rounded-lg border border-border/80 overflow-hidden">
      <div className="px-3 py-2.5 bg-muted/30 flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{title}</p>
          {partyName ? <p className="text-[11px] text-muted-foreground truncate">{partyName}</p> : null}
        </div>
        <p className="text-sm font-semibold tabular shrink-0">TDS {formatInr(totalTds)}</p>
      </div>
      {(rows || []).length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border/60">
                <th className="text-left py-1.5 px-3">Line</th>
                <th className="text-left py-1.5 pr-2 min-w-[11rem]">Section / code</th>
                <th className="text-right py-1.5 pr-2">Gross</th>
                <th className="text-right py-1.5 pr-2">%</th>
                <th className="text-right py-1.5 px-3 min-w-[7rem]">TDS amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const nature = r.nature_code || "NONE";
                const opt = natureOptions.find((o) => o.value === nature);
                return (
                  <tr key={r.line_key || r.label} className="border-b border-border/40 align-top">
                    <td className="py-2 px-3">
                      <span className="block font-medium truncate max-w-[200px]">{r.label || r.line_key}</span>
                      <span className="block text-[10px] text-muted-foreground">
                        {r.line_kind === "goods" ? "Purchase" : r.line_kind === "expense" ? "Expense" : r.line_kind || "Line"}
                        {r.payable_code ? ` · ${r.payable_code}` : ""}
                        {r.manual ? " · edited" : ""}
                      </span>
                      {r.reason ? (
                        <span className="block text-[10px] text-muted-foreground truncate max-w-[240px] mt-0.5">{r.reason}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-2">
                      <select
                        className="flex h-8 w-full min-w-[10rem] rounded-md border border-input bg-background px-2 text-[11px]"
                        disabled={!canEdit}
                        value={nature}
                        onChange={(e) => onPatchLine?.(r.line_key, { nature_code: e.target.value })}
                      >
                        {natureOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                        {opt?.payment_codes || r.payment_code || "—"}
                      </p>
                    </td>
                    <td className="py-2 pr-2 text-right tabular whitespace-nowrap">{formatInr(r.gross_amount)}</td>
                    <td className="py-2 pr-2 text-right tabular">{Number(r.tds_pct || 0)}</td>
                    <td className="py-2 px-3 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="h-8 text-right tabular text-xs"
                        disabled={!canEdit || nature === "NONE"}
                        value={nature === "NONE" ? "" : (r.tds_amount ?? "")}
                        onChange={(e) => onPatchLine?.(r.line_key, { tds_amount: e.target.value })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StepFrame({ title, icon: Icon, description, children, testId }) {
  return (
    <section className="rounded-xl lg:rounded-md border border-border bg-card overflow-hidden" data-testid={testId}>
      <header className="px-4 py-3 border-b border-border/70 bg-gradient-to-b from-bansal-ivory/40 to-transparent flex items-start gap-3 min-w-0">
        {Icon && (
          <span className="h-9 w-9 rounded-full bg-bansal-sageLight/60 text-primary grid place-items-center shrink-0">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0">
          <h4 className="font-sans text-sm font-semibold text-foreground leading-tight">{title}</h4>
          {description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>}
        </div>
      </header>
      <div className="p-4 space-y-4 min-w-0">{children}</div>
    </section>
  );
}

function InfoNote({ tone = "info", children }) {
  const tones = {
    info: "border-sky-200 bg-sky-50/70 text-sky-950",
    warning: "border-amber-200 bg-amber-50/70 text-amber-950",
    danger: "border-red-200 bg-red-50/70 text-red-900",
  };
  return (
    <div className={`rounded-lg lg:rounded-md border px-3 py-2.5 text-xs leading-relaxed ${tones[tone]}`}>
      {children}
    </div>
  );
}

function FreightSummary({ fs }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 min-w-0">
      <FreightStat label="Agreed freight" value={formatInr(fs.gross_freight)} />
      <FreightStat label="Shortfall" value={`${fs.weight_short_qtl} qtl`} tone="warning" />
      <FreightStat label="Deduction" value={formatInr(fs.freight_deduction)} tone="warning" />
      <FreightStat label="Net payable" value={formatInr(fs.net_freight_payable)} tone="sage" emphasis />
    </div>
  );
}

function FreightStat({ label, value, tone, emphasis }) {
  const toneClass = tone === "sage" ? "text-primary" : tone === "warning" ? "text-amber-800" : "text-foreground";
  return (
    <div className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2.5 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`text-sm mt-1 tabular break-words ${emphasis ? "font-semibold" : ""} ${toneClass}`}>{value}</p>
    </div>
  );
}

function ContractAllocationChips({ purchase, dense = false }) {
  const allocs = purchase?.contract_allocations || [];
  if (!allocs.length) {
    const variety = purchase?.variety;
    if (!variety) {
      return <p className="text-xs text-muted-foreground mt-1.5">No contract linked</p>;
    }
    return <p className="text-xs text-muted-foreground break-words mt-1.5">{variety}</p>;
  }
  return (
    <ul className={`min-w-0 ${dense ? "mt-1.5 space-y-1" : "mt-2 space-y-1.5"}`}>
      {allocs.map((a) => (
        <li
          key={a.purchase_contract_id ?? a.contract_number}
          className={
            dense
              ? "text-xs min-w-0 break-words leading-snug"
              : "text-xs rounded-md border border-border/70 bg-muted/15 px-2.5 py-1.5 min-w-0 break-words"
          }
        >
          <span className="font-mono font-medium">{a.contract_number || a.purchase_contract_id}</span>
          {a.variety ? (
            <span className="block text-muted-foreground mt-0.5">{a.variety}</span>
          ) : null}
          <span className="block text-muted-foreground tabular mt-0.5">
            {a.rate_per_qtl != null ? `₹${a.rate_per_qtl}/qtl` : "—"}
            {a.net_weight_qtl != null && Number(a.net_weight_qtl) > 0
              ? ` · ${a.net_weight_qtl} qtl split`
              : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TruckWeightSummary({ purchase: p, draftPartyNetQtl, formPartyNetQtl }) {
  const allocs = p?.contract_allocations || [];
  const formParty = formPartyNetQtl !== "" && formPartyNetQtl != null ? Number(formPartyNetQtl) : null;
  const partySaved = formParty != null && formParty > 0
    ? true
    : p?.party_net_weight_qtl != null && Number(p.party_net_weight_qtl) > 0;
  const billableTotal = allocs.reduce((s, a) => s + (Number(a.billable_reserved_qtl) || 0), 0);
  const showBillable = partySaved && billableTotal > 0;
  const draftActive = !partySaved && draftPartyNetQtl != null && Number(draftPartyNetQtl) > 0;

  let partyValue = "—";
  if (formParty != null && formParty > 0) {
    partyValue = `${formParty.toFixed(2)} qtl`;
  } else if (partySaved) {
    partyValue = `${p.party_net_weight_qtl} qtl`;
  } else if (draftActive) {
    partyValue = `${Number(draftPartyNetQtl).toFixed(2)} qtl`;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-md border border-border bg-muted/20 p-3 min-w-0">
      <WeightStat label="Our net" value={p.net_weight_qtl != null ? `${p.net_weight_qtl} qtl` : "—"} />
      <WeightStat
        label="Party net"
        value={partyValue}
        highlight={partySaved}
        muted={draftActive}
        hint={draftActive ? "unsaved" : undefined}
      />
      <WeightStat label="Bags" value={p.bags_reported ?? "—"} />
      <WeightStat
        label="Billable reserved"
        value={showBillable ? `${billableTotal.toFixed(2)} qtl` : partySaved ? "—" : "After save"}
        highlight={showBillable}
      />
    </div>
  );
}

function WeightStat({ label, value, highlight, muted, hint }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`text-sm font-medium tabular mt-0.5 break-words ${
          highlight ? "text-primary" : muted ? "text-amber-800" : "text-foreground"
        }`}
      >
        {value}
        {hint && <span className="text-[10px] font-normal text-muted-foreground ml-1">({hint})</span>}
      </p>
    </div>
  );
}
